import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Container, Table, Button, Badge, Form, InputGroup, Dropdown, Modal, Spinner, Alert, Offcanvas, Tabs, Tab, Row, Col, ListGroup, Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  useGetContactsQuery,
  useCreateContactMutation,
  useCreateLeadMutation,
  useCreateClientMutation,
  useCreateDealMutation,
  useUpdateContactMutation,
  useDeleteContactMutation,
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useGetEmailsQuery,
  useGetDealsQuery,
  useGetLeadsQuery,
  useGetClientsQuery,
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
} from '../services/api';
import { DEFAULT_RULES_ENGINE_CONFIG, normalizeRulesEngineConfig } from '../constants/rulesEngine';
import { buildEmailThreadsForEntity, mergeEmailResponses } from '../utils/crmEmailThreads';
import { useUser } from '../contexts/UserContext';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import {
  getDateKeyInTimezone,
  getRelativeDayDifferenceInTimezone,
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleTimeStringInTimezone,
} from '../utils/tenantTimezone';
import { mergeLabelCatalogCandidates } from '../utils/crmLabels';
import {
  formatHtmlDateValueToUsStorage,
  getDaysUntilContractRenewalDate,
  normalizeClientDateToUsStorage,
} from '../utils/crmContractRenewal';
import {
  Search,
  Plus,
  Folder,
  Lightning,
  ChevronDown,
  ThreeDotsVertical,
  Gear,
  Person,
  X,
  Envelope,
  Phone,
  Tag,
  InfoCircle,
  CurrencyDollar,
  Calendar,
  Clock,
  Briefcase,
  CheckSquare,
  ArrowRight,
  Dot,
  BuildingFill,
  GeoAltFill,
  Linkedin,
  PeopleFill,
  FileText,
  TypeBold,
  TypeItalic,
  TypeUnderline,
  Link45deg,
  At,
  ListUl,
  ListOl,
  ArrowLeft,
  TypeStrikethrough,
  Pin,
  Paperclip,
  Inbox,
  Archive,
  Check,
  Trash,
  Star,
  StarFill,
  Funnel,
  Diamond,
  DiamondFill
} from 'react-bootstrap-icons';

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  visibility?: 'global' | 'personal';
  owner?: string;
  title?: string;
  accountId?: string;
  organization?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  notes?: ContactNote[];
  tags?: string[];
  accountStage?: string;
  contactStage?: string;
  clientId?: string;
  mailboxStatus?: 'inbox' | 'archived';
  value?: string;
  expectedCloseDate?: string;
  contractRenewalDate?: string;
  businessUnit?: string;
  inStore?: boolean;
  businessType?: string;
  totalLocations?: string;
  rfpDate?: string;
  rfpExpirationDate?: string;
  securedLocations?: string;
  industry?: string;
  addedOnLinkedIn?: string;
  kkPlan?: string;
  goal?: string;
  found?: string;
  challenges?: string;
  timing?: string;
  dealScore?: string;
  dealSource?: string;
  eyeballs?: string;
  sourceOrigin?: string;
  sourceChannel?: string;
  sourceChannelId?: string;
  linkedInProfile?: string;
  numberOfEmployees?: string;
  annualRevenue?: string;
  decisionMaker?: boolean;
  workPhone?: string;
  cellPhone?: string;
  assistantName?: string;
  assistantPhone?: string;
  assistantEmail?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ContactNote {
  id: string;
  content: string;
  timestamp: string;
  author: string;
  pinned?: boolean;
  pinnedAt?: string;
}

interface ScheduledMeetingActivity {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  contactId?: string;
  meetingType?: 'in-person' | 'video' | 'phone';
  tags?: string[];
}

interface ActivityPurposeOption {
  id: string;
  name: string;
  color?: string;
}

interface DetailedActivityFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  meetingType: 'in-person' | 'video' | 'phone';
  meetingUrl: string;
  clientId: string;
  contactId: string;
  dealId: string;
  leadId: string;
  purposeTypeId: string;
  notes: string;
  priority: 'low' | 'normal' | 'high';
  freeBusy: 'free' | 'busy';
  markAsDone: boolean;
  videoCallProvider: '' | 'zoom' | 'teams' | 'google-meet';
}

type ScheduleActivityType =
  | 'call'
  | 'text'
  | 'meeting'
  | 'task'
  | 'deadline'
  | 'email'
  | 'lunch';

type QuickSchedulePreset = 'in1h' | 'in3h' | 'tomorrow' | 'nextWeek' | 'other';

type LabelsFieldId = 'detailsLabels' | 'personLabels' | 'organizationLabels';
type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';

type ContactEditableFieldId =
  | 'value'
  | 'expectedCloseDate'
  | 'contractRenewalDate'
  | 'country'
  | 'businessUnit'
  | 'businessType'
  | 'totalLocations'
  | 'rfpDate'
  | 'rfpExpirationDate'
  | 'securedLocations'
  | 'industry'
  | 'addedOnLinkedIn'
  | 'kkPlan'
  | 'goal'
  | 'found'
  | 'challenges'
  | 'timing'
  | 'dealScore'
  | 'dealSource'
  | 'eyeballs'
  | 'firstName'
  | 'lastName'
  | 'contactStage'
  | 'title'
  | 'email'
  | 'phone'
  | 'sourceOrigin'
  | 'sourceChannel'
  | 'sourceChannelId'
  | 'owner'
  | 'visibility'
  | 'organization'
  | 'accountStage'
  | 'address'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'linkedInProfile'
  | 'numberOfEmployees'
  | 'annualRevenue'
  | 'workPhone'
  | 'cellPhone'
  | 'assistantName'
  | 'assistantPhone'
  | 'assistantEmail';

type ContactEditableFieldConfig = {
  contactKey: keyof Contact;
  inputType: 'text' | 'date';
  placeholder: string;
};

const CONTACT_EDITABLE_FIELD_CONFIG: Record<ContactEditableFieldId, ContactEditableFieldConfig> = {
  value: { contactKey: 'value', inputType: 'text', placeholder: '$ Value' },
  expectedCloseDate: { contactKey: 'expectedCloseDate', inputType: 'date', placeholder: 'Expected close date' },
  contractRenewalDate: { contactKey: 'contractRenewalDate', inputType: 'date', placeholder: 'Contract Renewal' },
  country: { contactKey: 'country', inputType: 'text', placeholder: 'Country' },
  businessUnit: { contactKey: 'businessUnit', inputType: 'text', placeholder: 'Business Unit' },
  businessType: { contactKey: 'businessType', inputType: 'text', placeholder: 'Business Type' },
  totalLocations: { contactKey: 'totalLocations', inputType: 'text', placeholder: 'Total Locations' },
  rfpDate: { contactKey: 'rfpDate', inputType: 'date', placeholder: 'RFP Date' },
  rfpExpirationDate: { contactKey: 'rfpExpirationDate', inputType: 'date', placeholder: 'RFP Expiration Date' },
  securedLocations: { contactKey: 'securedLocations', inputType: 'text', placeholder: 'Secured Locations' },
  industry: { contactKey: 'industry', inputType: 'text', placeholder: 'Industry' },
  addedOnLinkedIn: { contactKey: 'addedOnLinkedIn', inputType: 'text', placeholder: 'Added on Linkedin' },
  kkPlan: { contactKey: 'kkPlan', inputType: 'text', placeholder: 'KK Plan' },
  goal: { contactKey: 'goal', inputType: 'text', placeholder: 'Goal' },
  found: { contactKey: 'found', inputType: 'text', placeholder: 'Found' },
  challenges: { contactKey: 'challenges', inputType: 'text', placeholder: 'Challenges' },
  timing: { contactKey: 'timing', inputType: 'text', placeholder: 'Timing' },
  dealScore: { contactKey: 'dealScore', inputType: 'text', placeholder: 'Deal Score' },
  dealSource: { contactKey: 'dealSource', inputType: 'text', placeholder: 'Deal Source' },
  eyeballs: { contactKey: 'eyeballs', inputType: 'text', placeholder: 'Eyeballs' },
  firstName: { contactKey: 'firstName', inputType: 'text', placeholder: 'First name' },
  lastName: { contactKey: 'lastName', inputType: 'text', placeholder: 'Last name' },
  contactStage: { contactKey: 'contactStage', inputType: 'text', placeholder: 'Contact Stage' },
  title: { contactKey: 'title', inputType: 'text', placeholder: 'Title' },
  email: { contactKey: 'email', inputType: 'text', placeholder: 'No email' },
  phone: { contactKey: 'phone', inputType: 'text', placeholder: 'No phone' },
  sourceOrigin: { contactKey: 'sourceOrigin', inputType: 'text', placeholder: 'Source origin' },
  sourceChannel: { contactKey: 'sourceChannel', inputType: 'text', placeholder: 'Source channel' },
  sourceChannelId: { contactKey: 'sourceChannelId', inputType: 'text', placeholder: 'Source channel ID' },
  owner: { contactKey: 'owner', inputType: 'text', placeholder: 'Owner' },
  visibility: { contactKey: 'visibility', inputType: 'text', placeholder: 'Visibility' },
  organization: { contactKey: 'organization', inputType: 'text', placeholder: 'No organization' },
  accountStage: { contactKey: 'accountStage', inputType: 'text', placeholder: 'Account Stage' },
  address: { contactKey: 'address', inputType: 'text', placeholder: 'Address' },
  city: { contactKey: 'city', inputType: 'text', placeholder: 'City' },
  state: { contactKey: 'state', inputType: 'text', placeholder: 'State' },
  zipCode: { contactKey: 'zipCode', inputType: 'text', placeholder: 'ZIP code' },
  linkedInProfile: { contactKey: 'linkedInProfile', inputType: 'text', placeholder: 'LinkedIn profile' },
  numberOfEmployees: { contactKey: 'numberOfEmployees', inputType: 'text', placeholder: 'Number of employees' },
  annualRevenue: { contactKey: 'annualRevenue', inputType: 'text', placeholder: 'Annual revenue' },
  workPhone: { contactKey: 'workPhone', inputType: 'text', placeholder: 'Work phone' },
  cellPhone: { contactKey: 'cellPhone', inputType: 'text', placeholder: 'Cell phone' },
  assistantName: { contactKey: 'assistantName', inputType: 'text', placeholder: "Assistant's Name" },
  assistantPhone: { contactKey: 'assistantPhone', inputType: 'text', placeholder: "Assistant's Phone" },
  assistantEmail: { contactKey: 'assistantEmail', inputType: 'text', placeholder: "Assistant's Email" },
};

const detailFieldIconStyle: React.CSSProperties = {
  width: '14px',
  height: '14px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const getSharedLabelColors = (): Record<string, string> => {
  const defaultColors: Record<string, string> = {
    HOT: 'danger',
    COLD: 'primary',
    WARM: 'warning',
    'QUARTER 2': 'secondary',
    'QUARTER 1': 'info',
    'QUARTER 3': 'info',
    'QUARTER 4': 'warning',
    NTTM: 'info',
    FRANCHISE: 'success',
    '1-10 LOCATIONS': 'warning',
    CHAINS: 'success',
    'I.P.M.': 'secondary'
  };

  try {
    const stored = localStorage.getItem('labelColors');
    if (stored) {
      const storedColors = JSON.parse(stored);
      return { ...defaultColors, ...storedColors };
    }
  } catch {
    // ignore storage parsing issues and fall back to defaults
  }

  return defaultColors;
};

const SCHEDULE_ACTIVITY_LABELS: Record<ScheduleActivityType, string> = {
  call: 'Call',
  text: 'Text',
  meeting: 'Meeting',
  task: 'Task',
  deadline: 'Deadline',
  email: 'Email',
  lunch: 'Lunch',
};

const SCHEDULE_ACTIVITY_OPTIONS: Array<{ type: ScheduleActivityType; label: string }> = [
  { type: 'call', label: 'Call' },
  { type: 'text', label: 'Text' },
  { type: 'meeting', label: 'Meeting' },
  { type: 'task', label: 'Task' },
  { type: 'deadline', label: 'Deadline' },
  { type: 'email', label: 'Email' },
  { type: 'lunch', label: 'Lunch' },
];

const DEFAULT_ACTIVITY_PURPOSE_OPTIONS: ActivityPurposeOption[] = [
  { id: 'internal-sales-meeting', name: 'Internal Sales Meeting', color: '#ff8c42' },
  { id: 'internal-developer-meetings', name: 'Internal Developer Meetings', color: '#6f42c1' },
  { id: 'client-sales-meetings', name: 'Client Sales Meetings', color: '#0d6efd' },
];

const DEFAULT_ACTIVITY_PURPOSE_ID = 'client-sales-meetings';
const ACTIVITY_CALENDAR_VISIBLE_HOURS = 12;
const ACTIVITY_CALENDAR_SLOT_HEIGHT = 30;
const ACTIVITY_CALENDAR_PANEL_WIDTH = 320;
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const CONTACT_OWNER_FAVORITES_STORAGE_KEY = 'contactOwnerFavorites';

const normalizeOwnerKey = (value: string): string =>
  String(value || '')
    .replace(/\(\s*you\s*\)/gi, '')
    .trim()
    .toLowerCase();

const formatActivityCalendarHourLabel = (hour: number): string => {
  const normalizedHour = ((hour % 24) + 24) % 24;
  const meridiem = normalizedHour >= 12 ? 'PM' : 'AM';
  const twelveHour = normalizedHour % 12 === 0 ? 12 : normalizedHour % 12;
  return `${twelveHour} ${meridiem}`;
};

const IconOnlyDropdownToggle = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ children, onClick, className, style, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    className={`btn btn-outline-secondary btn-sm ${className || ''}`.trim()}
    onClick={(event) => {
      event.preventDefault();
      onClick?.(event);
    }}
    style={{
      width: '32px',
      height: '32px',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderColor: '#ced4da',
      ...style
    }}
    {...rest}
  >
    {children}
  </button>
));
IconOnlyDropdownToggle.displayName = 'IconOnlyDropdownToggle';

const Contacts: React.FC = () => {
  const { currentUser } = useUser();
  const { data: contactsData, isLoading: isLoadingContacts, error: contactsError, refetch: refetchContacts } = useGetContactsQuery({});
  const { data: meetingsData, refetch: refetchMeetings } = useGetMeetingsQuery({});
  const { data: rulesEngineConfigData } = useGetRulesEngineConfigQuery(undefined);
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const timezoneOption = tenantTimezone ? { timeZone: tenantTimezone } : {};
  const [createContact, { isLoading: isCreatingContact }] = useCreateContactMutation();
  const [createLead] = useCreateLeadMutation();
  const [createClient] = useCreateClientMutation();
  const [createDeal] = useCreateDealMutation();
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateMeetingMutation();
  const { data: accountsData, refetch: refetchAccounts } = useGetAccountsQuery({});
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();

  const getDaysUntilContractRenewal = (contact: Contact): number | null =>
    getDaysUntilContractRenewalDate(contact.contractRenewalDate);
  const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation();
  const [deleteContact, { isLoading: isDeletingContact }] = useDeleteContactMutation();
  
  const contacts = contactsData || [];
  const accounts = accountsData || [];
  const accountNameMap = useMemo(() => {
    const map = new Map<string, any>();
    (accounts || []).forEach((account: any) => {
      const name = String(account?.name || '').trim();
      if (!name) return;
      map.set(name.toLowerCase(), account);
    });
    return map;
  }, [accounts]);
  const meetings = meetingsData || [];
  const rulesEngineConfig = useMemo(
    () => normalizeRulesEngineConfig(rulesEngineConfigData || DEFAULT_RULES_ENGINE_CONFIG),
    [rulesEngineConfigData]
  );
  const accountStageOptions = rulesEngineConfig.accountStages;
  const contactStageOptions = rulesEngineConfig.contactStages;
  
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isConvertingContact, setIsConvertingContact] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');
  const contactEmailAddress = String(selectedContact?.email || '').trim().toLowerCase();
  const shouldLoadContactEmailThreads = showSidebar && activeTab === 'email' && contactEmailAddress.length > 0;
  const {
    data: contactInboxEmailThreadsData,
    isLoading: isLoadingContactInboxEmailThreads,
    isFetching: isRefreshingContactInboxEmailThreads,
    error: contactInboxEmailThreadsError,
    refetch: refetchContactInboxEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'inbox',
      limit: 200,
    },
    {
      skip: !shouldLoadContactEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const {
    data: contactSentEmailThreadsData,
    isLoading: isLoadingContactSentEmailThreads,
    isFetching: isRefreshingContactSentEmailThreads,
    error: contactSentEmailThreadsError,
    refetch: refetchContactSentEmailThreads
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'sent',
      limit: 200,
    },
    {
      skip: !shouldLoadContactEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );
  const contactEmailThreadsData = useMemo(
    () => mergeEmailResponses(contactInboxEmailThreadsData, contactSentEmailThreadsData),
    [contactInboxEmailThreadsData, contactSentEmailThreadsData]
  );
  const isLoadingContactEmailThreads = isLoadingContactInboxEmailThreads || isLoadingContactSentEmailThreads;
  const isRefreshingContactEmailThreads = isRefreshingContactInboxEmailThreads || isRefreshingContactSentEmailThreads;
  const contactEmailThreadsError = contactInboxEmailThreadsError && contactSentEmailThreadsError ? contactInboxEmailThreadsError : null;
  const refetchContactEmailThreads = (): void => {
    refetchContactInboxEmailThreads();
    refetchContactSentEmailThreads();
  };
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showCustomizeColumnsModal, setShowCustomizeColumnsModal] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [selectedOwner, setSelectedOwner] = useState<string>(DEFAULT_OWNER_FILTER_LABEL);
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(CONTACT_OWNER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.map((entry) => normalizeOwnerKey(String(entry || ''))).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [labelsFilterSearchQuery, setLabelsFilterSearchQuery] = useState('');
  const [contractRenewalFilter, setContractRenewalFilter] = useState<'all' | 'overdue' | 'next30' | 'next60' | 'next90'>('all');
  const [contactMailboxView, setContactMailboxView] = useState<'inbox' | 'archive'>('inbox');
  const [showAllFields, setShowAllFields] = useState(false);
  const [showAllPersonFields, setShowAllPersonFields] = useState(false);
  const [showAllOrganizationFields, setShowAllOrganizationFields] = useState(false);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const [editingDetailFieldId, setEditingDetailFieldId] = useState<string | null>(null);
  const [detailFieldDraftValue, setDetailFieldDraftValue] = useState('');
  const [showLabelsDropdownField, setShowLabelsDropdownField] = useState<LabelsFieldId | null>(null);
  const [labelSearchQuery, setLabelSearchQuery] = useState('');
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('customLabels');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const noteEditorRef = useRef<HTMLDivElement | null>(null);
  const [isNoteEditorFocused, setIsNoteEditorFocused] = useState(false);
  const [currentNoteContent, setCurrentNoteContent] = useState('');
  const [notes, setNotes] = useState<ContactNote[]>([]);
  const [scheduledActivityType, setScheduledActivityType] = useState<ScheduleActivityType>('call');
  const [scheduledActivityText, setScheduledActivityText] = useState(SCHEDULE_ACTIVITY_LABELS.call);
  const [showScheduledActivityTypeDropdown, setShowScheduledActivityTypeDropdown] = useState(false);
  const [showActivityCalendar, setShowActivityCalendar] = useState(false);
  const [showCalendarToggleTooltip, setShowCalendarToggleTooltip] = useState(false);
  const [activityNowTick, setActivityNowTick] = useState(() => Date.now());
  const [activitySchedulerForm, setActivitySchedulerForm] = useState<DetailedActivityFormData>(() => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return {
      title: 'Call',
      description: '',
      startTime: toLocal(now),
      endTime: toLocal(end),
      location: '',
      meetingType: 'phone',
      meetingUrl: '',
      clientId: '',
      contactId: '',
      dealId: '',
      leadId: '',
      purposeTypeId: DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  });
  const [activityDealLeadSearch, setActivityDealLeadSearch] = useState('');
  const [showActivityDealLeadDropdown, setShowActivityDealLeadDropdown] = useState(false);
  const [activityPeopleSearch, setActivityPeopleSearch] = useState('');
  const [showActivityPeopleDropdown, setShowActivityPeopleDropdown] = useState(false);
  const [activityOrganizationSearch, setActivityOrganizationSearch] = useState('');
  const [showActivityOrganizationDropdown, setShowActivityOrganizationDropdown] = useState(false);
  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);
  const selfOwnerFilterLabel = useMemo(
    () => `${selfOwnerBaseLabel} (you)`,
    [selfOwnerBaseLabel]
  );
  const detailOwnerOptions = useMemo(
    () => [DEFAULT_OWNER_FILTER_LABEL, selfOwnerBaseLabel],
    [selfOwnerBaseLabel]
  );

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    contactStage: '',
    accountStage: '',
    visibility: 'global' as 'global' | 'personal',
    title: '',
    organization: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    notes: '',
    tags: '',
    clientId: '',
    decisionMaker: false
  });

  const shouldLoadActivitySchedulerLookups = showSidebar && activeTab === 'activity' && !!selectedContact;
  const { data: dealsData } = useGetDealsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: leadsData } = useGetLeadsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });
  const { data: clientsData } = useGetClientsQuery({}, { skip: !shouldLoadActivitySchedulerLookups });

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    contacts.forEach((contact: Contact) => {
      if (contact.tags && Array.isArray(contact.tags)) {
        contact.tags.forEach(tag => tagSet.add(tag));
      }
    });
    return Array.from(tagSet).sort();
  }, [contacts, selfOwnerFilterLabel]);

  const allAvailableLabels = useMemo(() => {
    const candidates: string[] = [
      ...Object.keys(getSharedLabelColors()),
      ...allTags,
      ...customLabels,
    ];
    return mergeLabelCatalogCandidates(candidates);
  }, [allTags, customLabels]);

  const meetingPurposeOptions = useMemo<ActivityPurposeOption[]>(() => {
    const persisted = (userPreferencesData as any)?.preferences?.crmMeetingPurposeTypes;
    if (!Array.isArray(persisted)) return DEFAULT_ACTIVITY_PURPOSE_OPTIONS;

    const seen = new Set<string>();
    const normalized: ActivityPurposeOption[] = [];
    persisted.forEach((item: any) => {
      if (!item || typeof item !== 'object') return;
      const name = String(item.name || item.label || '').trim().replace(/\s+/g, ' ');
      if (!name) return;
      const id = String(item.id || name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (!id || seen.has(id)) return;
      seen.add(id);
      normalized.push({
        id,
        name,
        color: String(item.color || '').trim() || undefined,
      });
    });

    return normalized.length > 0 ? normalized : DEFAULT_ACTIVITY_PURPOSE_OPTIONS;
  }, [userPreferencesData]);

  useEffect(() => {
    if (meetingPurposeOptions.length === 0) return;
    setActivitySchedulerForm((prev) => {
      if (meetingPurposeOptions.some((option) => option.id === prev.purposeTypeId)) return prev;
      return {
        ...prev,
        purposeTypeId: meetingPurposeOptions[0].id,
      };
    });
  }, [meetingPurposeOptions]);

  const activityDealRecords = useMemo(
    () => (Array.isArray(dealsData) ? (dealsData as Array<{ id: string; name?: string }>) : []),
    [dealsData]
  );
  const activityLeadRecords = useMemo(
    () => (
      Array.isArray(leadsData)
        ? (leadsData as Array<{ id: string; name?: string; organization?: string }>)
        : []
    ),
    [leadsData]
  );
  const activityContactRecords = useMemo(
    () => (Array.isArray(contacts) ? (contacts as Array<{ id: string; firstName?: string; lastName?: string; organization?: string; email?: string }>) : []),
    [contacts]
  );
  const activityClientRecords = useMemo(
    () => (Array.isArray(clientsData) ? (clientsData as Array<{ id: string; name?: string; organization?: string }>) : []),
    [clientsData]
  );

  const filteredActivityDealsAndLeads = useMemo(() => {
    const query = activityDealLeadSearch.toLowerCase().trim();
    const dealItems = activityDealRecords.map((deal) => ({
      id: deal.id,
      type: 'deal' as const,
      label: deal.name || 'Untitled deal',
    }));
    const leadItems = activityLeadRecords.map((lead) => ({
      id: lead.id,
      type: 'lead' as const,
      label: lead.organization ? `${lead.name || 'Untitled'} (${lead.organization})` : (lead.name || 'Untitled lead'),
    }));
    const allItems = [...dealItems, ...leadItems];
    if (!query) return allItems.slice(0, 12);
    return allItems.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 12);
  }, [activityDealRecords, activityLeadRecords, activityDealLeadSearch]);

  const activityDealLeadDisplayLabel = useMemo(() => {
    if (activitySchedulerForm.dealId) {
      const deal = activityDealRecords.find((item) => item.id === activitySchedulerForm.dealId);
      return deal?.name || 'Deal';
    }
    if (activitySchedulerForm.leadId) {
      const lead = activityLeadRecords.find((item) => item.id === activitySchedulerForm.leadId);
      if (!lead) return 'Lead';
      return lead.organization ? `${lead.name || ''} (${lead.organization})` : (lead.name || 'Lead');
    }
    return '';
  }, [activitySchedulerForm.dealId, activitySchedulerForm.leadId, activityDealRecords, activityLeadRecords]);

  const filteredActivityContacts = useMemo(() => {
    const query = activityPeopleSearch.toLowerCase().trim();
    const list = activityContactRecords.map((contact) => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      const label = contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Untitled contact');
      const searchText = `${fullName} ${contact.organization || ''} ${contact.email || ''}`.toLowerCase();
      return { id: contact.id, label, searchText };
    });
    if (!query) return list.slice(0, 12).map(({ id, label }) => ({ id, label }));
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12).map(({ id, label }) => ({ id, label }));
  }, [activityContactRecords, activityPeopleSearch]);

  const activityContactDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.contactId) return '';
    const contact = activityContactRecords.find((item) => item.id === activitySchedulerForm.contactId);
    if (!contact) return 'Contact';
    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    return contact.organization ? `${fullName} (${contact.organization})` : (fullName || 'Contact');
  }, [activitySchedulerForm.contactId, activityContactRecords]);

  const filteredActivityOrganizations = useMemo(() => {
    const query = activityOrganizationSearch.toLowerCase().trim();
    const list = activityClientRecords.map((client) => {
      const label = client.name || client.organization || 'Organization';
      return {
        id: client.id,
        label,
        searchText: `${label}`.toLowerCase(),
      };
    });
    if (!query) return list.slice(0, 12);
    return list.filter((item) => item.searchText.includes(query)).slice(0, 12);
  }, [activityClientRecords, activityOrganizationSearch]);

  const activityOrganizationDisplayLabel = useMemo(() => {
    if (!activitySchedulerForm.clientId) return '';
    const client = activityClientRecords.find((item) => item.id === activitySchedulerForm.clientId);
    return client?.name || client?.organization || 'Organization';
  }, [activitySchedulerForm.clientId, activityClientRecords]);

  const parseContactNotes = (rawNotes: unknown, fallbackTimestamp?: string): ContactNote[] => {
    if (typeof rawNotes === 'string') {
      const content = rawNotes.trim();
      if (!content) return [];
      const timestamp = String(fallbackTimestamp || new Date().toISOString());
      return [
        {
          id: `note-${timestamp}-legacy`,
          content,
          timestamp,
          author: 'PhiLL Kross',
          pinned: false,
        },
      ];
    }

    const notesArray = Array.isArray(rawNotes)
      ? rawNotes
      : (rawNotes && typeof rawNotes === 'object' ? [rawNotes] : []);

    if (!notesArray.length) return [];

    return notesArray
      .map((note, index) => {
        if (!note || typeof note !== 'object') return null;
        const noteRecord = note as Partial<ContactNote>;
        const content = String(noteRecord.content || '').trim();
        const timestamp = String(noteRecord.timestamp || fallbackTimestamp || '').trim();
        if (!content || !timestamp) return null;

        return {
          id: String(noteRecord.id || `note-${timestamp}-${index}`),
          content: String(noteRecord.content),
          timestamp,
          author: String(noteRecord.author || 'PhiLL Kross'),
          pinned: Boolean(noteRecord.pinned),
          pinnedAt: String(noteRecord.pinnedAt || '').trim() || undefined,
        } as ContactNote;
      })
      .filter((note): note is ContactNote => Boolean(note))
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  };

  const isContactArchived = (contact?: Contact | null): boolean =>
    String(contact?.mailboxStatus || '').trim().toLowerCase() === 'archived';

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, string>();
    ownerMap.set(normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL), DEFAULT_OWNER_FILTER_LABEL);
    ownerMap.set(normalizeOwnerKey(selfOwnerFilterLabel), selfOwnerFilterLabel);

    contacts.forEach((contact: Contact) => {
      const ownerLabel = String(contact.owner || '').trim();
      if (!ownerLabel) return;
      ownerMap.set(normalizeOwnerKey(ownerLabel), ownerLabel);
    });

    return Array.from(ownerMap.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => {
        if (a.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return -1;
        if (b.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [contacts]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const normalizedSelected = normalizeOwnerKey(selectedOwner);
    if (!selectedOwner || normalizedSelected === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      return DEFAULT_OWNER_FILTER_LABEL;
    }
    return String(selectedOwner).replace(/\(\s*you\s*\)/i, '').trim() || DEFAULT_OWNER_FILTER_LABEL;
  }, [selectedOwner]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [ownerOptions, normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    if (!favoriteOwnerKeys.length) return [];
    const favoriteKeySet = new Set(favoriteOwnerKeys.map((entry) => normalizeOwnerKey(entry)));
    return ownerOptions.filter((option) => favoriteKeySet.has(option.key));
  }, [ownerOptions, favoriteOwnerKeys]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const filteredContacts = useMemo(() => {
    let filtered = [...contacts];

    if (contactMailboxView === 'archive') {
      filtered = filtered.filter((contact: Contact) => isContactArchived(contact));
    } else {
      filtered = filtered.filter((contact: Contact) => !isContactArchived(contact));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((contact: Contact) => {
        const fullName = `${contact.firstName} ${contact.lastName}`.toLowerCase();
        const org = (contact.organization || '').toLowerCase();
        const email = (contact.email || '').toLowerCase();
        const phone = (contact.phone || '').toLowerCase();
        return fullName.includes(query) || org.includes(query) || email.includes(query) || phone.includes(query);
      });
    }

    if (selectedTag) {
      filtered = filtered.filter((contact: Contact) => 
        Array.isArray(contact.tags)
        && contact.tags.some((tag) => String(tag).trim().toLowerCase() === selectedTag.trim().toLowerCase())
      );
    }

    const normalizedSelectedOwner = normalizeOwnerKey(selectedOwner);
    if (normalizedSelectedOwner && normalizedSelectedOwner !== normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      filtered = filtered.filter((contact: Contact) => {
        const ownerLabel = String(contact.owner || '').trim();
        if (ownerLabel) return normalizeOwnerKey(ownerLabel) === normalizedSelectedOwner;
        if (normalizedSelectedOwner === normalizeOwnerKey(selfOwnerFilterLabel)) {
          return String(contact.visibility || '').toLowerCase() !== 'global';
        }
        return false;
      });
    }

    if (contractRenewalFilter !== 'all') {
      filtered = filtered.filter((contact: Contact) => {
        const days = getDaysUntilContractRenewal(contact);
        if (days === null) return false;
        if (contractRenewalFilter === 'overdue') return days < 0;
        if (contractRenewalFilter === 'next30') return days >= 0 && days <= 30;
        if (contractRenewalFilter === 'next60') return days >= 0 && days <= 60;
        if (contractRenewalFilter === 'next90') return days >= 0 && days <= 90;
        return true;
      });
    }

    if (contractRenewalFilter !== 'all') {
      filtered.sort((a: Contact, b: Contact) => {
        const aDays = getDaysUntilContractRenewal(a);
        const bDays = getDaysUntilContractRenewal(b);
        if (aDays === null && bDays === null) return 0;
        if (aDays === null) return 1;
        if (bDays === null) return -1;
        return aDays - bDays;
      });
    }

    return filtered;
  }, [contacts, contactMailboxView, searchQuery, selectedTag, selectedOwner, selfOwnerFilterLabel, contractRenewalFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(CONTACT_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerKeys));
    } catch {
      // Ignore persistence errors in private browsing modes.
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    if (normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    const selectedExists = ownerOptions.some((option) => option.key === normalizeOwnerKey(selectedOwner));
    if (!selectedExists) {
      setSelectedOwner(DEFAULT_OWNER_FILTER_LABEL);
    }
  }, [ownerOptions, selectedOwner]);

  const toggleFavoriteOwner = (ownerKey: string) => {
    const normalizedKey = normalizeOwnerKey(ownerKey);
    if (!normalizedKey || normalizedKey === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    setFavoriteOwnerKeys((prev) => {
      const normalizedPrev = prev.map((entry) => normalizeOwnerKey(entry));
      if (normalizedPrev.includes(normalizedKey)) {
        return prev.filter((entry) => normalizeOwnerKey(entry) !== normalizedKey);
      }
      return [...normalizedPrev, normalizedKey];
    });
  };

  useEffect(() => {
    if (!selectedContact) return;

    setNotes(parseContactNotes(selectedContact.notes, selectedContact.createdAt || selectedContact.updatedAt));
    setCurrentNoteContent('');
    setScheduledActivityType('call');
    setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS.call);
    setShowScheduledActivityTypeDropdown(false);
    setShowActivityCalendar(false);
    setShowCalendarToggleTooltip(false);
    setActivityNowTick(Date.now());
    setActivitySchedulerForm(
      createDefaultDetailedActivityFormData(selectedContact, 'call', SCHEDULE_ACTIVITY_LABELS.call)
    );
    setActivityDealLeadSearch('');
    setActivityPeopleSearch('');
    setActivityOrganizationSearch('');
    setShowActivityDealLeadDropdown(false);
    setShowActivityPeopleDropdown(false);
    setShowActivityOrganizationDropdown(false);
    setShowAllFields(false);
    setShowAllPersonFields(false);
    setShowAllOrganizationFields(false);
    setHoveredField(null);
    setEditingDetailFieldId(null);
    setDetailFieldDraftValue('');
    setShowLabelsDropdownField(null);
    setLabelSearchQuery('');

    if (noteEditorRef.current) {
      noteEditorRef.current.innerHTML = '';
      noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
    }
    setIsNoteEditorFocused(false);
  }, [selectedContact?.id]);

  useEffect(() => {
    setSelectedContacts(new Set());
    if (!selectedContact) return;

    const shouldCloseSidebar =
      (contactMailboxView === 'inbox' && isContactArchived(selectedContact))
      || (contactMailboxView === 'archive' && !isContactArchived(selectedContact));

    if (shouldCloseSidebar) {
      setShowSidebar(false);
      setSelectedContact(null);
    }
  }, [contactMailboxView, selectedContact]);

  const contactLabels = Array.isArray(selectedContact?.tags) ? (selectedContact?.tags || []) : [];
  const hasTags = contactLabels.length > 0;

  const toDateInputValue = (dateValue?: string): string => {
    if (!dateValue) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
    const usMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return getDateKeyInTimezone(parsed, tenantTimezone) || '';
    }
    return '';
  };

  const formatDateForStorage = (dateInput: string): string => {
    if (!dateInput) return '';
    const [year, month, day] = dateInput.split('-');
    if (!year || !month || !day) return '';
    return `${month}/${day}/${year}`;
  };

  const startEditingDetailField = (fieldId: ContactEditableFieldId) => {
    if (!selectedContact) return;
    const config = CONTACT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return;

    const currentValue = selectedContact[config.contactKey];
    if (config.inputType === 'date') {
      setDetailFieldDraftValue(toDateInputValue(typeof currentValue === 'string' ? currentValue : ''));
    } else if (fieldId === 'owner') {
      const explicitOwner = typeof currentValue === 'string' ? currentValue.trim() : '';
      if (explicitOwner) {
        setDetailFieldDraftValue(explicitOwner);
      } else {
        setDetailFieldDraftValue(selectedContact.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel);
      }
    } else if (fieldId === 'visibility') {
      setDetailFieldDraftValue(selectedContact.visibility === 'personal' ? 'personal' : 'global');
    } else if (fieldId === 'businessUnit') {
      const businessUnitValue = typeof currentValue === 'string' ? currentValue.trim() : '';
      setDetailFieldDraftValue(businessUnitValue || (selectedContact.inStore ? 'In-Store' : ''));
    } else {
      setDetailFieldDraftValue(typeof currentValue === 'string' ? currentValue : '');
    }
    setEditingDetailFieldId(fieldId);
  };

  const handleSaveDetailField = async (fieldId: ContactEditableFieldId) => {
    if (!selectedContact) return;
    const config = CONTACT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return;

    if (fieldId === 'businessUnit') {
      const currentBusinessUnit = String(selectedContact.businessUnit || '').trim() || (selectedContact.inStore ? 'In-Store' : '');
      const nextBusinessUnit = detailFieldDraftValue.trim();
      const nextInStore = nextBusinessUnit.toLowerCase() === 'in-store' || nextBusinessUnit.toLowerCase() === 'in store';

      if (currentBusinessUnit === nextBusinessUnit && Boolean(selectedContact.inStore) === nextInStore) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateContact({
          contactId: selectedContact.id,
          businessUnit: nextBusinessUnit,
          inStore: nextInStore
        } as any).unwrap();

        const nextContact: Contact = { ...selectedContact, businessUnit: nextBusinessUnit, inStore: nextInStore };
        setSelectedContact(nextContact);
        setEditingDetailFieldId(null);
        setHoveredField(null);
        refetchContacts();
      } catch (error: any) {
        console.error('Error updating business unit:', error);
        alert(`Failed to update Business Unit: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    if (fieldId === 'owner') {
      const currentVisibility = selectedContact.visibility === 'personal' ? 'personal' : 'global';
      const selectedOwner = String(detailFieldDraftValue || '').trim() || DEFAULT_OWNER_FILTER_LABEL;
      const nextVisibility = selectedOwner.toLowerCase() === DEFAULT_OWNER_FILTER_LABEL.toLowerCase() ? 'global' : 'personal';
      if (currentVisibility === nextVisibility) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateContact({
          contactId: selectedContact.id,
          visibility: nextVisibility
        } as any).unwrap();

        const nextContact: Contact = { ...selectedContact, visibility: nextVisibility };
        setSelectedContact(nextContact);
        setEditingDetailFieldId(null);
        setHoveredField(null);
        refetchContacts();
      } catch (error: any) {
        console.error('Error updating owner visibility mapping:', error);
        alert(`Failed to update Owner: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    if (fieldId === 'visibility') {
      const currentVisibility = selectedContact.visibility === 'personal' ? 'personal' : 'global';
      const nextVisibility = String(detailFieldDraftValue || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global';
      if (currentVisibility === nextVisibility) {
        setEditingDetailFieldId(null);
        return;
      }

      try {
        await updateContact({
          contactId: selectedContact.id,
          visibility: nextVisibility
        } as any).unwrap();

        const nextContact: Contact = { ...selectedContact, visibility: nextVisibility };
        setSelectedContact(nextContact);
        setEditingDetailFieldId(null);
        setHoveredField(null);
        refetchContacts();
      } catch (error: any) {
        console.error('Error updating visibility:', error);
        alert(`Failed to update Visibility: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    const currentValue = String(selectedContact[config.contactKey] || '');
    const nextValue = config.inputType === 'date'
      ? formatHtmlDateValueToUsStorage(detailFieldDraftValue)
      : detailFieldDraftValue.trim();
    const unchanged =
      config.inputType === 'date'
        ? normalizeClientDateToUsStorage(currentValue) === nextValue
        : currentValue === nextValue;
    if (unchanged) {
      setEditingDetailFieldId(null);
      return;
    }

    try {
      if (fieldId === 'organization') {
        const nextOrgName = String(nextValue || '').trim();
        const existingAccount = nextOrgName ? accountNameMap.get(nextOrgName.toLowerCase()) : null;
        let nextAccountId = '';

        if (nextOrgName) {
          if (existingAccount?.id) {
            nextAccountId = String(existingAccount.id);
          } else {
            const created = await createAccount({ name: nextOrgName }).unwrap();
            const createdAccount = (created?.data?.account || created?.account || null);
            nextAccountId = createdAccount?.id ? String(createdAccount.id) : '';
            refetchAccounts();
          }
        }

        await updateContact({
          contactId: selectedContact.id,
          organization: nextOrgName,
          accountId: nextAccountId || undefined,
        } as any).unwrap();

        const nextContact: Contact = {
          ...selectedContact,
          organization: nextOrgName,
          accountId: nextAccountId || undefined,
        };
        setSelectedContact(nextContact);
        setEditingDetailFieldId(null);
        setHoveredField(null);
        refetchContacts();
        return;
      }

      await updateContact({
        contactId: selectedContact.id,
        [config.contactKey]: nextValue
      } as any).unwrap();

      const nextContact: Contact = { ...selectedContact, [config.contactKey]: nextValue } as Contact;
      setSelectedContact(nextContact);
      setEditingDetailFieldId(null);
      setHoveredField(null);
      refetchContacts();
    } catch (error: any) {
      console.error(`Error updating ${String(config.contactKey)}:`, error);
      alert(`Failed to update ${config.placeholder}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const toggleContactLabel = async (label: string) => {
    if (!selectedContact) return;
    const currentLabels = Array.isArray(selectedContact.tags) ? selectedContact.tags : [];
    const isAssigned = currentLabels.includes(label);
    const updatedLabels = isAssigned
      ? currentLabels.filter((item) => item !== label)
      : [...currentLabels, label];

    try {
      await updateContact({
        contactId: selectedContact.id,
        tags: updatedLabels
      }).unwrap();
      setSelectedContact({ ...selectedContact, tags: updatedLabels });
      refetchContacts();
    } catch (error: any) {
      console.error('Error updating contact labels:', error);
      alert(`Failed to update labels: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCreateNewLabel = async () => {
    if (!selectedContact) return;
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );

    const finalLabel = existingLabel || nextLabel;
    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, finalLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    await toggleContactLabel(finalLabel);
  };

  const handleCreateFilterLabel = () => {
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );

    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, nextLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    setShowTagsFilter(false);
    setLabelsFilterSearchQuery('');
  };

  const renderEditableLabelsRow = (
    fieldId: LabelsFieldId,
    showAll: boolean
  ) => {
    if (!selectedContact) return null;

    const isOpen = showLabelsDropdownField === fieldId;
    const isHovered = hoveredField === fieldId;
    const showAddPrompt = isHovered && !hasTags;
    const shouldRender = showAll || hasTags || isOpen;
    if (!shouldRender) return null;

    const filteredLabelOptions = allAvailableLabels.filter((label) =>
      label.toLowerCase().includes(labelSearchQuery.toLowerCase())
    );

    return (
      <Dropdown
        key={fieldId}
        show={isOpen}
        onToggle={(nextIsOpen) => {
          if (nextIsOpen) {
            setShowLabelsDropdownField(fieldId);
          } else {
            setShowLabelsDropdownField(null);
            setLabelSearchQuery('');
          }
        }}
        className="mb-2"
        style={{ marginBottom: 0 }}
      >
        <Dropdown.Toggle
          as="div"
          className="text-decoration-none labels-dropdown-toggle"
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHovered ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            minHeight: '32px',
            border: 'none',
            textAlign: 'left'
          }}
          onMouseEnter={() => setHoveredField(fieldId)}
          onMouseLeave={() => setHoveredField(null)}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={detailFieldIconStyle}>
              <Tag size={14} />
            </span>
            {hasTags ? (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {contactLabels.map((label) => (
                  <Badge key={`${fieldId}-${label}`} bg={getLabelVariant(label)} style={{ fontSize: '0.55rem', padding: '0.15rem 0.35rem' }}>
                    {label}
                  </Badge>
                ))}
              </div>
            ) : (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: showAddPrompt ? '#0d6efd' : '#6c757d',
                  fontWeight: showAddPrompt ? 500 : 'normal'
                }}
              >
                {showAddPrompt ? '+ Add Labels' : 'Labels'}
              </span>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          show={isOpen}
          style={{
            minWidth: '250px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '0.5rem',
            marginTop: '0.25rem'
          }}
        >
          <div className="mb-2">
            <InputGroup size="sm">
              <InputGroup.Text>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search label"
                value={labelSearchQuery}
                onChange={(e) => setLabelSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{ fontSize: '0.75rem' }}
              />
            </InputGroup>
          </div>

          {filteredLabelOptions.map((label) => {
            const isAssigned = contactLabels.includes(label);
            return (
              <Dropdown.Item
                key={`${fieldId}-${label}-option`}
                active={isAssigned}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  await toggleContactLabel(label);
                }}
                style={{ fontSize: '0.75rem' }}
              >
                <div className="d-flex align-items-center">
                  {isAssigned && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                  <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                    {label}
                  </Badge>
                </div>
              </Dropdown.Item>
            );
          })}

          <Dropdown.Divider />
          <Dropdown.Item
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await handleCreateNewLabel();
            }}
            style={{ fontSize: '0.75rem', color: '#0d6efd' }}
          >
            <div className="d-flex align-items-center">
              <Plus className="me-2" size={14} />
              <span>Add new label</span>
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const renderEditableContactRow = (
    fieldId: ContactEditableFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
    options: { showAll: boolean; showInfo?: boolean }
  ) => {
    if (!selectedContact) return null;
    const config = CONTACT_EDITABLE_FIELD_CONFIG[fieldId];
    if (!config) return null;

    const rawValue = String(selectedContact[config.contactKey] || '').trim();
    const displayValue = fieldId === 'businessUnit'
      ? (rawValue || (selectedContact.inStore ? 'In-Store' : ''))
      : fieldId === 'owner'
        ? (rawValue || (selectedContact.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel))
        : fieldId === 'visibility'
          ? (selectedContact.visibility === 'personal' ? 'Personal (private)' : 'Global (visible for everyone)')
          : rawValue;
    const isEditing = editingDetailFieldId === fieldId;
    const isHovered = hoveredField === fieldId;
    const hasValue = displayValue.length > 0;
    const shouldRender =
      fieldId === 'accountStage'
      || fieldId === 'contactStage'
      || fieldId === 'owner'
      || fieldId === 'visibility'
      || options.showAll
      || hasValue
      || isEditing;
    if (!shouldRender) return null;
    const showAddPrompt = isHovered && !hasValue && !isEditing;

    const inputType = config.inputType === 'date'
      ? 'date'
      : fieldId.toLowerCase().includes('email')
        ? 'email'
        : fieldId.toLowerCase().includes('phone')
          ? 'tel'
          : 'text';
    const businessUnitOptions = Array.from(new Set(
      ['In-Store', String(selectedContact.businessUnit || '').trim()].filter(Boolean)
    ));
    const stageOptions = fieldId === 'accountStage'
      ? accountStageOptions
      : fieldId === 'contactStage'
        ? contactStageOptions
        : [];

    return (
      <div
        key={fieldId}
        className="mb-2"
        style={{
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s'
        }}
        onMouseEnter={() => setHoveredField(fieldId)}
        onMouseLeave={() => setHoveredField(null)}
        onClick={() => startEditingDetailField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={detailFieldIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (hasValue ? '#212529' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal'
            }}
          >
            {hasValue ? displayValue : (showAddPrompt ? '+ Add value' : label)}
          </span>
          {options.showInfo && <InfoCircle size={9} className="ms-1 text-muted" />}
        </div>
        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
            {fieldId === 'owner' ? (
              <Form.Select
                value={detailFieldDraftValue || (selectedContact.visibility === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel)}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                {detailOwnerOptions.map((option) => (
                  <option key={`contact-owner-option-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'visibility' ? (
              <Form.Select
                value={detailFieldDraftValue || (selectedContact.visibility === 'personal' ? 'personal' : 'global')}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="global">Global (visible for everyone)</option>
                <option value="personal">Personal (private)</option>
              </Form.Select>
            ) : (fieldId === 'accountStage' || fieldId === 'contactStage') ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {stageOptions.map((option) => (
                  <option key={`${fieldId}-stage-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'businessUnit' ? (
              <Form.Select
                value={detailFieldDraftValue}
                onChange={(event) => setDetailFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select business unit</option>
                {businessUnitOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type={inputType}
                value={detailFieldDraftValue}
                placeholder={label}
                onChange={(e) => setDetailFieldDraftValue(e.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            {fieldId !== 'owner' && fieldId !== 'visibility' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setDetailFieldDraftValue('');
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setEditingDetailFieldId(null);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleSaveDetailField(fieldId);
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Save
            </Button>
          </div>
        )}
      </div>
    );
  };

  const upcomingFocusActivities = useMemo(() => {
    if (!selectedContact) return [] as ScheduledMeetingActivity[];

    const now = Date.now();
    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => String(meeting.contactId || '') === selectedContact.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .filter((meeting) => {
        const startMs = new Date(meeting.startTime).getTime();
        return !Number.isNaN(startMs) && startMs >= (now - (5 * 60 * 1000));
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [meetings, selectedContact]);

  const contactActivityTimeline = useMemo(() => {
    if (!selectedContact) return [] as ScheduledMeetingActivity[];

    return (meetings as ScheduledMeetingActivity[])
      .filter((meeting) => String(meeting.contactId || '') === selectedContact.id)
      .filter((meeting) => typeof meeting.startTime === 'string' && meeting.startTime.length > 0)
      .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());
  }, [meetings, selectedContact]);

  const contactEmailThreads = useMemo(
    () => buildEmailThreadsForEntity(contactEmailThreadsData, contactEmailAddress),
    [contactEmailThreadsData, contactEmailAddress]
  );

  const pinnedFocusNotes = useMemo(() => {
    return [...notes]
      .filter((note) => note.pinned)
      .sort((a, b) => {
        const aPinnedAt = new Date(a.pinnedAt || a.timestamp).getTime();
        const bPinnedAt = new Date(b.pinnedAt || b.timestamp).getTime();
        return bPinnedAt - aPinnedAt;
      });
  }, [notes]);

  const handleSelectContact = (contactId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedContacts.size === filteredContacts.length) {
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(filteredContacts.map((c: Contact) => c.id)));
    }
  };

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setShowSidebar(true);
  };

  const handleConvertSelectedContact = async (target: 'Lead' | 'Client' | 'Deal') => {
    if (!selectedContact || isConvertingContact) return;

    const contactId = selectedContact.id;
    const fullName = `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim();
    const fallbackName = fullName || String(selectedContact.organization || '').trim() || String(selectedContact.email || '').trim() || 'Converted Contact';
    const visibility = selectedContact.visibility === 'personal' ? 'personal' : 'global';
    const parsedNotes = parseContactNotes(
      selectedContact.notes,
      selectedContact.createdAt || selectedContact.updatedAt
    );
    const notesText = parsedNotes
      .map((note) => String(note.content || '').trim())
      .filter(Boolean)
      .join('\n\n');

    setIsConvertingContact(true);
    try {
      if (target === 'Lead') {
        await createLead({
          name: fallbackName,
          title: selectedContact.title || undefined,
          organization: selectedContact.organization || undefined,
          accountStage: selectedContact.accountStage || undefined,
          contactStage: selectedContact.contactStage || undefined,
          value: selectedContact.value || undefined,
          expectedCloseDate: selectedContact.expectedCloseDate || undefined,
          sourceOrigin: selectedContact.sourceOrigin || undefined,
          sourceChannel: selectedContact.sourceChannel || undefined,
          sourceChannelId: selectedContact.sourceChannelId || undefined,
          businessUnit: selectedContact.businessUnit || undefined,
          inStore: Boolean(selectedContact.inStore),
          businessType: selectedContact.businessType || undefined,
          totalLocations: selectedContact.totalLocations || undefined,
          rfpDate: selectedContact.rfpDate || undefined,
          rfpExpirationDate: selectedContact.rfpExpirationDate || undefined,
          securedLocations: selectedContact.securedLocations || undefined,
          industry: selectedContact.industry || undefined,
          addedOnLinkedIn: selectedContact.addedOnLinkedIn || undefined,
          kkPlan: selectedContact.kkPlan || undefined,
          goal: selectedContact.goal || undefined,
          found: selectedContact.found || undefined,
          challenges: selectedContact.challenges || undefined,
          timing: selectedContact.timing || undefined,
          dealScore: selectedContact.dealScore || undefined,
          dealSource: selectedContact.dealSource || undefined,
          eyeballs: selectedContact.eyeballs || undefined,
          country: selectedContact.country || undefined,
          address: selectedContact.address || undefined,
          linkedInProfile: selectedContact.linkedInProfile || undefined,
          numberOfEmployees: selectedContact.numberOfEmployees || undefined,
          annualRevenue: selectedContact.annualRevenue || undefined,
          email: selectedContact.email || undefined,
          phone: selectedContact.phone || undefined,
          notes: parsedNotes,
          status: 'Lead',
          visibility,
        }).unwrap();
      } else if (target === 'Client') {
        await createClient({
          name: fallbackName,
          organization: selectedContact.organization || undefined,
          accountStage: selectedContact.accountStage || undefined,
          contactStage: selectedContact.contactStage || undefined,
          industry: selectedContact.industry || undefined,
          address: selectedContact.address || undefined,
          city: selectedContact.city || undefined,
          state: selectedContact.state || undefined,
          zipCode: selectedContact.zipCode || undefined,
          country: selectedContact.country || undefined,
          phone: selectedContact.phone || undefined,
          email: selectedContact.email || undefined,
          notes: notesText || undefined,
          status: 'current',
          value: selectedContact.value || undefined,
          expectedCloseDate: selectedContact.expectedCloseDate || undefined,
          businessUnit: selectedContact.businessUnit || undefined,
          inStore: Boolean(selectedContact.inStore),
          sourceOrigin: selectedContact.sourceOrigin || undefined,
          sourceChannel: selectedContact.sourceChannel || undefined,
          sourceChannelId: selectedContact.sourceChannelId || undefined,
          linkedInProfile: selectedContact.linkedInProfile || undefined,
          numberOfEmployees: selectedContact.numberOfEmployees || undefined,
          annualRevenue: selectedContact.annualRevenue || undefined,
          visibility,
        }).unwrap();
      } else {
        const dealName = fullName
          ? `${fullName}${selectedContact.organization ? ` - ${selectedContact.organization}` : ''}`
          : (selectedContact.organization || 'Converted Contact Deal');
        await createDeal({
          name: dealName,
          value: selectedContact.value || '0',
          expectedCloseDate: selectedContact.expectedCloseDate || undefined,
          clientId: selectedContact.clientId || undefined,
          contactId: selectedContact.id,
          notes: notesText || undefined,
          visibility,
        }).unwrap();
      }

      await deleteContact(contactId).unwrap();
      setSelectedContacts((prev) => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
      setShowSidebar(false);
      setSelectedContact(null);
      refetchContacts();
      alert(`Converted contact to ${target.toLowerCase()}.`);
    } catch (error: any) {
      alert(`Failed to convert contact to ${target.toLowerCase()}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    } finally {
      setIsConvertingContact(false);
    }
  };

  const handleSaveContact = async () => {
    try {
      const initialContactNoteText = String(formData.notes || '').trim();
      const initialContactNotes = initialContactNoteText
        ? [
            {
              id: `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              content: initialContactNoteText,
              timestamp: new Date().toISOString(),
              author: 'PhiLL Kross',
              pinned: false,
            } as ContactNote,
          ]
        : undefined;

      const contactData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactStage: formData.contactStage || undefined,
        accountStage: formData.accountStage || undefined,
        title: formData.title || undefined,
        organization: formData.organization || undefined,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        address: formData.address || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        country: formData.country || undefined,
        notes: initialContactNotes,
        tags: formData.tags ? [formData.tags] : [],
        clientId: formData.clientId || undefined,
        visibility: formData.visibility === 'personal' ? 'personal' : 'global',
        decisionMaker: Boolean(formData.decisionMaker)
      };

      await createContact(contactData).unwrap();
      
      setFormData({
        firstName: '',
        lastName: '',
        contactStage: '',
        accountStage: '',
        visibility: 'global',
        title: '',
        organization: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        notes: '',
        tags: '',
        clientId: '',
        decisionMaker: false
      });

      setShowAddContactModal(false);
      refetchContacts();
    } catch (error: any) {
      console.error('Error creating contact:', error);
      alert(`Failed to create contact: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleFormChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    const relativeDayDiff = getRelativeDayDifferenceInTimezone(date, new Date(), tenantTimezone);
    if (relativeDayDiff === 0) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    } else if (relativeDayDiff === -1) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption })}`;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', ...timezoneOption }) +
      ' at ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', ...timezoneOption });
  };

  const getLabelVariant = (label: string): string => {
    const currentColors = getSharedLabelColors();
    return currentColors[label] || currentColors[label.toUpperCase()] || 'secondary';
  };

  const executeCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    if (noteEditorRef.current) noteEditorRef.current.focus();
  };

  const renderScheduledActivityIcon = (activityType: ScheduleActivityType) => {
    if (activityType === 'call' || activityType === 'text') return <Phone size={12} />;
    if (activityType === 'email') return <Envelope size={12} />;
    if (activityType === 'meeting' || activityType === 'deadline') return <Calendar size={12} />;
    if (activityType === 'task') return <CheckSquare size={12} />;
    return <Briefcase size={12} />;
  };

  const getScheduledActivityTypeFromMeeting = (meeting: ScheduledMeetingActivity): ScheduleActivityType => {
    const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
    const activityTag = tags.find((tag) => typeof tag === 'string' && tag.startsWith('activity:'));
    if (activityTag) {
      const parsed = activityTag.replace('activity:', '').trim() as ScheduleActivityType;
      if (SCHEDULE_ACTIVITY_LABELS[parsed]) {
        return parsed;
      }
    }

    if (meeting.meetingType === 'phone') return 'call';
    return 'meeting';
  };

  const formatFocusDateLabel = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...timezoneOption
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      ...timezoneOption
    });
  };

  const getDetailedActivityDefaultStartEnd = () => {
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    return { start: toLocal(now), end: toLocal(end) };
  };

  const createDefaultDetailedActivityFormData = (
    contact: Contact | null,
    activityType: ScheduleActivityType,
    titleOverride?: string
  ): DetailedActivityFormData => {
    const { start, end } = getDetailedActivityDefaultStartEnd();
    const title = String(titleOverride || '').trim() || SCHEDULE_ACTIVITY_LABELS[activityType];
    return {
      title,
      description: '',
      startTime: start,
      endTime: end,
      location: String(contact?.organization || '').trim(),
      meetingType: (activityType === 'call' || activityType === 'text') ? 'phone' : 'in-person',
      meetingUrl: '',
      clientId: String(contact?.clientId || ''),
      contactId: String(contact?.id || ''),
      dealId: '',
      leadId: '',
      purposeTypeId: meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID,
      notes: '',
      priority: 'normal',
      freeBusy: 'free',
      markAsDone: false,
      videoCallProvider: '',
    };
  };

  const handleActivitySchedulerFieldChange = (field: keyof DetailedActivityFormData, value: string | boolean) => {
    setActivitySchedulerForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectDetailedActivityType = (activityType: ScheduleActivityType) => {
    const nextLabel = SCHEDULE_ACTIVITY_LABELS[activityType];
    setScheduledActivityType(activityType);
    setScheduledActivityText(nextLabel);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      title: nextLabel,
      meetingType:
        activityType === 'call' || activityType === 'text'
          ? 'phone'
          : (prev.videoCallProvider ? 'video' : 'in-person'),
    }));
  };

  const getActivitySchedulerDisplayDate = () => {
    if (activitySchedulerForm.startTime) {
      const parsed = new Date(activitySchedulerForm.startTime);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  };

  const shiftActivitySchedulerDay = (delta: number) => {
    const nextDate = getActivitySchedulerDisplayDate();
    nextDate.setDate(nextDate.getDate() + delta);
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${nextDate.getFullYear()}-${pad(nextDate.getMonth() + 1)}-${pad(nextDate.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const handleActivitySchedulerPrevDay = () => shiftActivitySchedulerDay(-1);
  const handleActivitySchedulerNextDay = () => shiftActivitySchedulerDay(1);
  const handleActivitySchedulerToday = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const datePrefix = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T`;
    const fallback = getDetailedActivityDefaultStartEnd();
    const startTime = activitySchedulerForm.startTime || fallback.start;
    const endTime = activitySchedulerForm.endTime || fallback.end;
    const startClock = startTime.slice(11, 16);
    const endClock = endTime.slice(11, 16);
    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: `${datePrefix}${startClock}`,
      endTime: `${datePrefix}${endClock}`,
    }));
  };

  const activityCalendarStartHour = useMemo(() => {
    const displayDate = getActivitySchedulerDisplayDate();
    const now = new Date(activityNowTick);
    const isDisplayToday =
      displayDate.getDate() === now.getDate()
      && displayDate.getMonth() === now.getMonth()
      && displayDate.getFullYear() === now.getFullYear();
    const selectedStart = new Date(activitySchedulerForm.startTime);
    const hasSelectedStart = !Number.isNaN(selectedStart.getTime());
    const sameDayAsDisplay =
      hasSelectedStart
      && selectedStart.getDate() === displayDate.getDate()
      && selectedStart.getMonth() === displayDate.getMonth()
      && selectedStart.getFullYear() === displayDate.getFullYear();

    const selectedHour = sameDayAsDisplay ? selectedStart.getHours() : displayDate.getHours();
    const nowHour = now.getHours();
    let proposedStart = selectedHour - 4;

    if (isDisplayToday && sameDayAsDisplay && Math.abs(selectedHour - nowHour) < ACTIVITY_CALENDAR_VISIBLE_HOURS) {
      proposedStart = Math.min(selectedHour, nowHour) - 1;
    }

    if (isDisplayToday) {
      const minStartForNow = nowHour - ACTIVITY_CALENDAR_VISIBLE_HOURS + 1;
      const maxStartForNow = nowHour;
      proposedStart = Math.max(proposedStart, minStartForNow);
      proposedStart = Math.min(proposedStart, maxStartForNow);
    }

    return Math.max(0, Math.min(24 - ACTIVITY_CALENDAR_VISIBLE_HOURS, proposedStart));
  }, [activitySchedulerForm.startTime, activityNowTick]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActivityNowTick(Date.now());
    }, 60000);
    return () => window.clearInterval(intervalId);
  }, []);

  const handleActivityCalendarGridClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const relativeY = Math.max(0, Math.min(rect.height - 1, event.clientY - rect.top));
    const slotOffset = relativeY / ACTIVITY_CALENDAR_SLOT_HEIGHT;
    const hourOffset = Math.floor(slotOffset);
    const minutes = (slotOffset - hourOffset) >= 0.5 ? 30 : 0;
    const clickedHour = Math.min(23, activityCalendarStartHour + hourOffset);

    const displayDate = getActivitySchedulerDisplayDate();
    const startDate = new Date(displayDate);
    startDate.setHours(clickedHour, minutes, 0, 0);

    const existingStart = new Date(activitySchedulerForm.startTime);
    const existingEnd = new Date(activitySchedulerForm.endTime);
    const fallbackDurationMs = 60 * 60 * 1000;
    const existingDurationMs =
      !Number.isNaN(existingStart.getTime())
      && !Number.isNaN(existingEnd.getTime())
      && existingEnd.getTime() > existingStart.getTime()
        ? (existingEnd.getTime() - existingStart.getTime())
        : fallbackDurationMs;
    const endDate = new Date(startDate.getTime() + existingDurationMs);

    setActivitySchedulerForm((prev) => ({
      ...prev,
      startTime: toLocalDateTimeInputValue(startDate),
      endTime: toLocalDateTimeInputValue(endDate),
    }));
  };

  const handleCancelDetailedActivity = () => {
    if (!selectedContact) return;
    setActivitySchedulerForm(
      createDefaultDetailedActivityFormData(selectedContact, scheduledActivityType, scheduledActivityText)
    );
    setActivityDealLeadSearch('');
    setActivityPeopleSearch('');
    setActivityOrganizationSearch('');
    setShowActivityDealLeadDropdown(false);
    setShowActivityPeopleDropdown(false);
    setShowActivityOrganizationDropdown(false);
  };

  const handleSelectScheduledActivityType = (nextType: ScheduleActivityType) => {
    setScheduledActivityType(nextType);
    setScheduledActivityText(SCHEDULE_ACTIVITY_LABELS[nextType]);
    setShowScheduledActivityTypeDropdown(false);
  };

  const toLocalDateTimeInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getQuickScheduleStart = (preset: QuickSchedulePreset): Date | null => {
    const now = new Date();
    if (preset === 'in1h') return new Date(now.getTime() + (60 * 60 * 1000));
    if (preset === 'in3h') return new Date(now.getTime() + (3 * 60 * 60 * 1000));
    if (preset === 'tomorrow') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      return tomorrow;
    }
    if (preset === 'nextWeek') {
      const nextWeek = new Date(now);
      nextWeek.setDate(nextWeek.getDate() + 7);
      nextWeek.setHours(9, 0, 0, 0);
      return nextWeek;
    }

    const proposedDate = new Date(now.getTime() + (60 * 60 * 1000));
    const userInput = window.prompt(
      'Schedule date and time (YYYY-MM-DDTHH:mm)',
      toLocalDateTimeInputValue(proposedDate)
    );
    if (!userInput) return null;

    const parsed = new Date(userInput);
    if (Number.isNaN(parsed.getTime())) {
      alert('Invalid date/time format. Please use YYYY-MM-DDTHH:mm');
      return null;
    }
    return parsed;
  };

  const handleQuickScheduleActivity = async (preset: QuickSchedulePreset) => {
    if (!selectedContact) return;

    const startDate = getQuickScheduleStart(preset);
    if (!startDate) return;

    const title = scheduledActivityText.trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const endDate = new Date(startDate.getTime() + (30 * 60 * 1000));
    const isPhoneActivity = scheduledActivityType === 'call' || scheduledActivityType === 'text';

    try {
      await createMeeting({
        title,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        meetingType: isPhoneActivity ? 'phone' : 'in-person',
        contactId: selectedContact.id,
        location: selectedContact.organization || undefined,
        tags: [`activity:${scheduledActivityType}`],
        visibility: 'personal',
      }).unwrap();

      refetchMeetings();
      refetchContacts();
    } catch (error: any) {
      console.error('Error scheduling activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveDetailedActivity = async () => {
    if (!selectedContact) return;

    const currentContact = selectedContact;
    const title = String(activitySchedulerForm.title || '').trim() || SCHEDULE_ACTIVITY_LABELS[scheduledActivityType];
    const startDate = new Date(activitySchedulerForm.startTime);
    const endDate = new Date(activitySchedulerForm.endTime);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      alert('Please set a valid activity date and time.');
      return;
    }

    const purposeTag = `purpose:${activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID)}`;
    const tags = Array.from(new Set([`activity:${scheduledActivityType}`, purposeTag]));

    try {
      await createMeeting({
        title,
        description: activitySchedulerForm.description || undefined,
        startTime: startDate.toISOString(),
        endTime: endDate.toISOString(),
        location: activitySchedulerForm.location || undefined,
        meetingType: activitySchedulerForm.videoCallProvider ? 'video' : activitySchedulerForm.meetingType,
        meetingUrl: activitySchedulerForm.meetingUrl || undefined,
        clientId: activitySchedulerForm.clientId || currentContact.clientId || undefined,
        contactId: activitySchedulerForm.contactId || currentContact.id,
        dealId: activitySchedulerForm.dealId || undefined,
        leadId: activitySchedulerForm.leadId || undefined,
        purposeTypeId: activitySchedulerForm.purposeTypeId || (meetingPurposeOptions[0]?.id || DEFAULT_ACTIVITY_PURPOSE_ID),
        notes: activitySchedulerForm.notes || undefined,
        tags,
        visibility: 'personal',
      }).unwrap();

      refetchMeetings();
      refetchContacts();
      setActivitySchedulerForm(
        createDefaultDetailedActivityFormData(currentContact, scheduledActivityType, SCHEDULE_ACTIVITY_LABELS[scheduledActivityType])
      );
      setActivityDealLeadSearch('');
      setActivityPeopleSearch('');
      setActivityOrganizationSearch('');
      setShowActivityDealLeadDropdown(false);
      setShowActivityPeopleDropdown(false);
      setShowActivityOrganizationDropdown(false);
    } catch (error: any) {
      console.error('Error creating detailed activity:', error);
      alert(`Failed to schedule activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveNote = async () => {
    if (!selectedContact) return;
    const content = noteEditorRef.current?.innerHTML || currentNoteContent;
    const textContent = noteEditorRef.current?.textContent || currentNoteContent.replace(/<[^>]*>/g, '');
    if (!textContent.trim()) return;

    const newNote: ContactNote = {
      id: Date.now().toString(),
      content,
      timestamp: new Date().toISOString(),
      author: 'PhiLL Kross',
      pinned: false
    };
    const nextNotes = [newNote, ...notes];

    try {
      await updateContact({
        contactId: selectedContact.id,
        notes: nextNotes
      }).unwrap();

      setNotes(nextNotes);
      setSelectedContact((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      setCurrentNoteContent('');
      if (noteEditorRef.current) {
        noteEditorRef.current.innerHTML = '';
        noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
      }
      setIsNoteEditorFocused(false);
      refetchContacts();
    } catch (error: any) {
      console.error('Error saving note:', error);
      alert(`Failed to save note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleTogglePinNote = async (noteId: string) => {
    if (!selectedContact) return;

    const nowIso = new Date().toISOString();
    const nextNotes = notes.map((note) => {
      if (note.id !== noteId) return note;
      const nextPinned = !note.pinned;
      return {
        ...note,
        pinned: nextPinned,
        pinnedAt: nextPinned ? nowIso : undefined
      };
    });

    try {
      await updateContact({
        contactId: selectedContact.id,
        notes: nextNotes
      }).unwrap();
      setNotes(nextNotes);
      setSelectedContact((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      refetchContacts();
    } catch (error: any) {
      console.error('Error toggling note pin:', error);
      alert(`Failed to update note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!selectedContact) return;
    const shouldDelete = window.confirm('Delete this note?');
    if (!shouldDelete) return;

    const nextNotes = notes.filter((note) => note.id !== noteId);
    try {
      await updateContact({
        contactId: selectedContact.id,
        notes: nextNotes
      }).unwrap();
      setNotes(nextNotes);
      setSelectedContact((prev) => (prev ? { ...prev, notes: nextNotes } : prev));
      refetchContacts();
    } catch (error: any) {
      console.error('Error deleting note:', error);
      alert(`Failed to delete note: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleArchiveSelectedContact = async () => {
    if (!selectedContact) return;

    const currentlyArchived = isContactArchived(selectedContact);
    const nextMailboxStatus: Contact['mailboxStatus'] = currentlyArchived ? 'inbox' : 'archived';
    const confirmationMessage = currentlyArchived
      ? `Move "${selectedContact.firstName} ${selectedContact.lastName}" back to Inbox?`
      : `Archive "${selectedContact.firstName} ${selectedContact.lastName}"?`;

    const shouldProceed = window.confirm(confirmationMessage);
    if (!shouldProceed) return;

    try {
      await updateContact({
        contactId: selectedContact.id,
        mailboxStatus: nextMailboxStatus
      }).unwrap();

      const nextContact: Contact = { ...selectedContact, mailboxStatus: nextMailboxStatus };
      setSelectedContact(nextContact);
      if (
        (contactMailboxView === 'inbox' && nextMailboxStatus === 'archived')
        || (contactMailboxView === 'archive' && nextMailboxStatus !== 'archived')
      ) {
        setShowSidebar(false);
        setSelectedContact(null);
      }
      refetchContacts();
    } catch (error: any) {
      console.error('Error updating contact mailbox status:', error);
      alert(`Failed to update contact status: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedFromList = async () => {
    const ids = Array.from(selectedContacts);
    if (ids.length === 0) return;
    const count = ids.length;
    const shouldDelete = window.confirm(
      count === 1 ? 'Delete the selected contact? This cannot be undone.' : `Delete ${count} selected contacts? This cannot be undone.`
    );
    if (!shouldDelete) return;

    try {
      for (const id of ids) {
        await deleteContact(id).unwrap();
      }
      setSelectedContacts(new Set());
      if (selectedContact && ids.includes(selectedContact.id)) {
        setShowSidebar(false);
        setSelectedContact(null);
      }
      refetchContacts();
      alert(count === 1 ? 'Contact deleted.' : `${count} contacts deleted.`);
    } catch (error: any) {
      alert(`Failed to delete: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleDeleteSelectedContact = async () => {
    if (!selectedContact) return;

    const fullName = `${selectedContact.firstName} ${selectedContact.lastName}`.trim();
    const shouldDelete = window.confirm(`Delete contact "${fullName}"? This cannot be undone.`);
    if (!shouldDelete) return;

    try {
      await deleteContact(selectedContact.id).unwrap();
      setSelectedContacts((prev) => {
        const next = new Set(prev);
        next.delete(selectedContact.id);
        return next;
      });
      setShowSidebar(false);
      setSelectedContact(null);
      refetchContacts();
    } catch (error: any) {
      console.error('Error deleting contact:', error);
      alert(`Failed to delete contact: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleCancelNote = () => {
    setCurrentNoteContent('');
    if (noteEditorRef.current) {
      noteEditorRef.current.innerHTML = '';
      noteEditorRef.current.setAttribute('data-placeholder', 'Take a note, @name...');
    }
    setIsNoteEditorFocused(false);
  };

  if (isLoadingContacts) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (contactsError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Error loading contacts</Alert.Heading>
          <p>{(contactsError as any)?.data?.message || 'Failed to load contacts'}</p>
          <Button onClick={() => refetchContacts()}>Retry</Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      {/* Page Header - same as Leads */}
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Contacts</h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <InputGroup size="sm" style={{ width: '200px' }}>
                <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search Kross Global"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                  size="sm"
                />
              </InputGroup>
            </div>
          </div>
        </Container>
      </div>

      <div className="py-3" style={{ fontSize: '0.75rem' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="contacts-inbox-view-tooltip">Inbox</Tooltip>}>
                <Button
                  variant={contactMailboxView === 'inbox' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setContactMailboxView('inbox')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Inbox size={14} />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="contacts-archive-view-tooltip">Archive</Tooltip>}>
                <Button
                  variant={contactMailboxView === 'archive' ? 'outline-primary' : 'outline-secondary'}
                  size="sm"
                  onClick={() => setContactMailboxView('archive')}
                  style={{
                    width: '40px',
                    height: '34px',
                    padding: 0,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    marginLeft: '-1px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Archive size={14} />
                </Button>
              </OverlayTrigger>
            </div>
            {selectedContacts.size > 0 && (
              <OverlayTrigger placement="bottom" overlay={<Tooltip id="contacts-delete-selected-tooltip">Delete selected</Tooltip>}>
                <span className="d-inline-block">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="d-flex align-items-center"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                    disabled={isDeletingContact}
                    onClick={handleDeleteSelectedFromList}
                  >
                    <Trash size={14} />
                  </Button>
                </span>
              </OverlayTrigger>
            )}
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              onClick={() => setShowAddContactModal(true)}
            >
              <Plus className="me-1" size={14} />
              Contact
              <ChevronDown className="ms-1" size={12} />
            </Button>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{filteredContacts.length} contacts</span>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Dropdown>
              <Dropdown.Toggle
                variant={contractRenewalFilter !== 'all' ? 'primary' : 'outline-secondary'}
                size="sm"
                id="contacts-contract-renewal-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {contractRenewalFilter === 'overdue'
                  ? 'Renewal: Overdue'
                  : contractRenewalFilter === 'next30'
                    ? 'Renewal: Next 30'
                    : contractRenewalFilter === 'next60'
                      ? 'Renewal: Next 60'
                      : contractRenewalFilter === 'next90'
                        ? 'Renewal: Next 90'
                        : 'Renewal: All'}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setContractRenewalFilter('all')}>All</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('overdue')}>Overdue / expired</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next30')}>Next 30 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next60')}>Next 60 days</Dropdown.Item>
                <Dropdown.Item onClick={() => setContractRenewalFilter('next90')}>Next 90 days</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showTagsFilter}
              onToggle={(nextIsOpen) => {
                setShowTagsFilter(nextIsOpen);
                if (!nextIsOpen) setLabelsFilterSearchQuery('');
              }}
            >
              <Dropdown.Toggle variant="outline-secondary" size="sm" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                {selectedTag || 'All labels'}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                <div className="mb-2">
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search label"
                      value={labelsFilterSearchQuery}
                      onChange={(e) => setLabelsFilterSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedTag('');
                    setShowTagsFilter(false);
                    setLabelsFilterSearchQuery('');
                  }}
                  style={{ fontSize: '0.75rem' }}
                >
                  <div className="d-flex align-items-center">
                    {!selectedTag && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                    <span>All labels</span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                {allAvailableLabels
                  .filter((label) => label.toLowerCase().includes(labelsFilterSearchQuery.toLowerCase()))
                  .map((label) => (
                    <Dropdown.Item
                      key={label}
                      onClick={() => {
                        setSelectedTag(label);
                        setShowTagsFilter(false);
                        setLabelsFilterSearchQuery('');
                      }}
                      style={{ fontSize: '0.75rem' }}
                    >
                      <div className="d-flex align-items-center">
                        {selectedTag === label && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                        <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                          {label}
                        </Badge>
                      </div>
                    </Dropdown.Item>
                  ))}
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateFilterLabel();
                  }}
                  style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                >
                  <div className="d-flex align-items-center">
                    <Plus className="me-2" size={14} />
                    <span>Add new label</span>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showOwnerFilter}
              autoClose="outside"
              onToggle={(isOpen) => {
                setShowOwnerFilter(isOpen);
                if (isOpen) {
                  setOwnerFilterSearchQuery('');
                  setOwnerFilterTab('owners');
                  setHoveredOwnerRowKey(null);
                } else {
                  setHoveredOwnerRowKey(null);
                }
              }}
            >
              <Dropdown.Toggle variant="outline-secondary" size="sm" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}>
                {selectedOwnerButtonLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
                <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search owner or filter"
                      value={ownerFilterSearchQuery}
                      onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                      style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('favorites')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Star size={15} />
                    <span>Favorites</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('owners')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Person size={15} />
                    <span>Owners</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('filters')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Funnel size={15} />
                    <span>Filters</span>
                  </button>
                </div>
                <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
                  {ownerFilterTab === 'owners' && (
                    <div className="py-1">
                      {visibleOwnerOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No owners found.
                        </div>
                      ) : (
                        visibleOwnerOptions.map((ownerOption) => {
                          const isEveryone = ownerOption.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);
                          const isFavorite = favoriteOwnerKeys.some((entry) => normalizeOwnerKey(entry) === ownerOption.key);
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          const isRowHovered = hoveredOwnerRowKey === ownerOption.key;
                          const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                          return (
                            <button
                              key={ownerOption.key}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              onMouseEnter={() => setHoveredOwnerRowKey(ownerOption.key)}
                              onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === ownerOption.key ? null : prev))}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem',
                                borderRadius: '0.35rem',
                                boxSizing: 'border-box',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                                <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {isEveryone ? (
                                  <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                    <Star size={14} />
                                  </span>
                                ) : (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteOwner(ownerOption.key);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleFavoriteOwner(ownerOption.key);
                                      }
                                    }}
                                    style={{
                                      color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                      lineHeight: 1,
                                      display: 'inline-flex',
                                    }}
                                  >
                                    {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                  </span>
                                )}
                                {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {ownerFilterTab === 'filters' && (
                    <div className="py-1">
                      {[
                        { id: 'global', label: 'Global (everyone)', description: 'Only records visible to everyone' },
                        { id: 'personal', label: 'Personal (private)', description: 'Only personally scoped records' },
                      ]
                        .filter((option) => {
                          const query = ownerFilterSearchQuery.trim().toLowerCase();
                          if (!query) return true;
                          return `${option.label} ${option.description}`.toLowerCase().includes(query);
                        })
                        .map((option) => {
                          const isSelected =
                            option.id === 'global'
                              ? normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)
                              : normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(selfOwnerFilterLabel);
                          return (
                            <button
                              key={`contact-owner-filter-${option.id}`}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(option.id === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerFilterLabel);
                                setShowOwnerFilter(false);
                              }}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '0.5rem',
                                padding: '0.6rem 0.75rem',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{option.label}</div>
                                <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>{option.description}</div>
                              </div>
                              {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                            </button>
                          );
                        })}
                    </div>
                  )}
                  {ownerFilterTab === 'favorites' && (
                    <div className="py-1">
                      {visibleFavoriteOwnerOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No favorite owners yet.
                        </div>
                      ) : (
                        visibleFavoriteOwnerOptions.map((ownerOption) => {
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          return (
                            <button
                              key={`contact-favorite-owner-${ownerOption.key}`}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: '#6c757d' }} />
                                <span style={{ fontWeight: 500, color: '#212529' }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <StarFill size={14} style={{ color: '#f4b400' }} />
                                {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="link" size="sm" className="p-2">
              <ThreeDotsVertical size={18} />
            </Button>
          </div>
        </div>

        <Card className="crm-table-card">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 crm-table" style={{ fontSize: '0.75rem' }}>
              <thead className="table-light">
                <tr>
                  <th style={{ width: '50px', fontSize: '0.75rem' }}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedContacts.size === filteredContacts.length && filteredContacts.length > 0}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th style={{ fontSize: '0.75rem' }}>Name</th>
                  <th style={{ width: '48px', fontSize: '0.75rem', whiteSpace: 'nowrap', textAlign: 'center' }} title="Decision Maker">DM</th>
                  <th style={{ fontSize: '0.75rem' }}>Title</th>
                  <th style={{ fontSize: '0.75rem' }}>Organization</th>
                  <th style={{ fontSize: '0.75rem' }}>Email</th>
                  <th style={{ fontSize: '0.75rem' }}>Phone</th>
                  <th style={{ fontSize: '0.75rem' }}>Tags</th>
                  <th style={{ fontSize: '0.75rem' }}>Contact created</th>
                  <th style={{ width: '40px', fontSize: '0.75rem' }}>
                    <Button variant="link" size="sm" className="p-0" onClick={() => setShowCustomizeColumnsModal(true)} style={{ fontSize: '0.75rem' }}>
                      <Gear size={14} />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5 text-muted" style={{ fontSize: '0.75rem' }}>No contacts found</td>
                  </tr>
                ) : (
          filteredContacts.map((contact: Contact) => (
            <tr 
              key={contact.id} 
              onClick={() => handleContactClick(contact)}
              style={{ cursor: 'pointer' }}
            >
              <td onClick={(e) => handleSelectContact(contact.id, e)}>
                <Form.Check
                  checked={selectedContacts.has(contact.id)}
                  onChange={() => {}}
                />
              </td>
              <td>{contact.firstName} {contact.lastName}</td>
              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                {contact.decisionMaker ? (
                  <span title="Decision Maker" style={{ color: '#0d6efd', display: 'inline-flex', alignItems: 'center' }}>
                    <DiamondFill size={14} />
                  </span>
                ) : null}
              </td>
              <td>{contact.title || '-'}</td>
              <td>{contact.organization || '-'}</td>
              <td>{contact.email || '-'}</td>
              <td>{contact.phone || '-'}</td>
              <td>
                <div className="d-flex gap-1 flex-wrap">
                  {contact.tags && contact.tags.map((tag, idx) => (
                    <Badge key={idx} bg="secondary" style={{ fontSize: '0.6rem', padding: '0.15rem 0.35rem' }}>
                      {tag}
                    </Badge>
                  ))}
                </div>
              </td>
              <td>
                {contact.createdAt 
                  ? toLocaleDateStringInTimezone(contact.createdAt, 'en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    }, tenantTimezone)
                  : '-'
                }
              </td>
              <td></td>
            </tr>
          ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

      {/* Add Contact Modal */}
      <Modal show={showAddContactModal} onHide={() => setShowAddContactModal(false)} size="lg">
        <Modal.Header style={{ fontSize: '1rem', padding: '0.75rem 1rem' }}>
          <Modal.Title>Add contact</Modal.Title>
          <Button variant="link" onClick={() => setShowAddContactModal(false)} style={{ padding: 0, fontSize: '18px' }}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.75rem', padding: '1rem' }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>First name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Last name *</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Contact Stage</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.contactStage}
                  onChange={(e) => handleFormChange('contactStage', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="">Select</option>
                  {contactStageOptions.map((stage) => (
                    <option key={`contact-contact-stage-${stage}`} value={stage}>
                      {stage}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Title</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.title}
                  onChange={(e) => handleFormChange('title', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Organization</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.organization}
                  onChange={(e) => handleFormChange('organization', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Account Stage</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.accountStage}
                  onChange={(e) => handleFormChange('accountStage', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="">Select</option>
                  {accountStageOptions.map((stage) => (
                    <option key={`contact-account-stage-${stage}`} value={stage}>
                      {stage}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Email</Form.Label>
                <Form.Control
                  type="email"
                  size="sm"
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Visibility</Form.Label>
                <Form.Select
                  size="sm"
                  value={formData.visibility}
                  onChange={(e) => handleFormChange('visibility', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <option value="global">Global (tenant-wide)</option>
                  <option value="personal">Personal (private)</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Phone</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Decision Maker</Form.Label>
                <div style={{ paddingTop: '0.25rem' }}>
                  <Form.Check
                    type="switch"
                    id="add-contact-decision-maker-switch"
                    label={formData.decisionMaker ? 'Yes' : 'No'}
                    checked={Boolean(formData.decisionMaker)}
                    onChange={(e) => handleFormChange('decisionMaker', e.target.checked)}
                    style={{ fontSize: '0.75rem' }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Address</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.address}
                  onChange={(e) => handleFormChange('address', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>City</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.city}
                  onChange={(e) => handleFormChange('city', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>State</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.state}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Zip Code</Form.Label>
                <Form.Control
                  size="sm"
                  value={formData.zipCode}
                  onChange={(e) => handleFormChange('zipCode', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label style={{ fontSize: '0.75rem' }}>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  size="sm"
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ fontSize: '0.75rem', padding: '0.75rem 1rem' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span style={{ fontSize: '0.75rem' }}>
              {contacts.length}/unlimited
            </span>
            <div className="d-flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setShowAddContactModal(false)}>
                Cancel
              </Button>
              <Button 
                variant="primary" 
                size="sm" 
                onClick={handleSaveContact}
                disabled={isCreatingContact || !formData.firstName || !formData.lastName}
              >
                {isCreatingContact ? <Spinner size="sm" animation="border" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Contact Detail Sidebar - same layout as Leads */}
      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="end"
        style={{
          width: 'calc(100vw - 510px)',
          maxWidth: '1200px',
          minWidth: '800px',
          top: '56px',
          height: 'calc(100vh - 56px)',
          zIndex: 1040
        }}
      >
        <Offcanvas.Header
          className="position-relative"
          style={{
            borderBottom: '1px solid #dee2e6',
            paddingTop: '0.95rem',
            paddingBottom: '1.05rem',
          }}
        >
          <Offcanvas.Title style={{ flex: 1 }}>
            {selectedContact && (
              <div>
                <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                  {`${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim() || 'Untitled contact'}
                </div>
                {String(selectedContact.organization || '').trim().length > 0 && (
                  <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                    {selectedContact.organization}
                  </div>
                )}
              </div>
            )}
          </Offcanvas.Title>
          <Button variant="link" className="p-0 position-absolute" onClick={() => { setShowSidebar(false); setSelectedContact(null); }} style={{ top: '1.4rem', right: '1rem', zIndex: 10 }}>
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {selectedContact && (
            <div className="d-flex h-100" style={{ overflowX: 'hidden' }}>
              <div className="crm-sidebar-left-column" style={{ width: '320px', padding: '1rem', borderRight: '1px solid #dee2e6', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                <div className="mb-4" style={{ flex: 1 }}>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>DETAILS</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllFields(!showAllFields)}>
                        {showAllFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>

                    {showAllFields && (
                      <div className="mb-2 d-flex align-items-center gap-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={detailFieldIconStyle}>
                          <FileText size={14} />
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Add to sequence</span>
                      </div>
                    )}

                    {renderEditableLabelsRow(
                      'detailsLabels',
                      showAllFields
                    )}

                    {renderEditableContactRow('value', '$ Value', CurrencyDollar, { showAll: showAllFields })}
                    {renderEditableContactRow('expectedCloseDate', 'Expected close date', Calendar, { showAll: showAllFields })}
                    {renderEditableContactRow('country', 'Country', CheckSquare, { showAll: showAllFields })}
                    {renderEditableContactRow('businessUnit', 'Business Unit:', Briefcase, { showAll: showAllFields })}
                    {renderEditableContactRow('businessType', 'Business Type:', CheckSquare, { showAll: showAllFields })}
                    {renderEditableContactRow('totalLocations', 'Total Locations:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('rfpDate', 'RFP Date:', Calendar, { showAll: showAllFields })}
                    {renderEditableContactRow('rfpExpirationDate', 'RFP Expiration Date:', Calendar, { showAll: showAllFields })}
                    {renderEditableContactRow('securedLocations', 'Secured Locations:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('industry', 'Industry:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('addedOnLinkedIn', 'Added on Linkedin:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('kkPlan', 'KK Plan:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('goal', 'Goal:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('found', 'Found:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('challenges', 'Challenges:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('timing', 'Timing:', FileText, { showAll: showAllFields })}
                    {renderEditableContactRow('dealScore', 'Deal Score:', CheckSquare, { showAll: showAllFields })}
                    {renderEditableContactRow('dealSource', 'Deal Source:', CheckSquare, { showAll: showAllFields })}
                    {renderEditableContactRow('eyeballs', 'Eyeballs', FileText, { showAll: showAllFields })}

                    {renderEditableContactRow('owner', 'Owner', Person, { showAll: true })}
                    {renderEditableContactRow('visibility', 'Visibility', PeopleFill, { showAll: true })}

                    {renderEditableContactRow('sourceOrigin', 'Source origin', ArrowRight, { showAll: showAllFields, showInfo: true })}
                    {renderEditableContactRow('sourceChannel', 'Source channel', ArrowRight, { showAll: showAllFields })}
                    {renderEditableContactRow('sourceChannelId', 'Source channel ID', Dot, { showAll: showAllFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>PERSON</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllPersonFields(!showAllPersonFields)}>
                        {showAllPersonFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>

                    {renderEditableContactRow('firstName', 'First name', Person, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('lastName', 'Last name', Person, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('title', 'Title', FileText, { showAll: showAllPersonFields })}
                    {selectedContact && (
                      <div className="d-flex align-items-center gap-2 mb-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={detailFieldIconStyle}>
                          {selectedContact.decisionMaker
                            ? <DiamondFill size={14} style={{ color: '#0d6efd' }} />
                            : <Diamond size={14} />}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#212529', flex: 1 }}>Decision Maker</span>
                        <Form.Check
                          type="switch"
                          id={`contact-decision-maker-switch-${selectedContact.id}`}
                          checked={Boolean(selectedContact.decisionMaker)}
                          onChange={async (e) => {
                            const next = e.target.checked;
                            if (Boolean(selectedContact.decisionMaker) === next) return;
                            try {
                              await updateContact({ contactId: selectedContact.id, decisionMaker: next } as any).unwrap();
                              setSelectedContact({ ...selectedContact, decisionMaker: next });
                            } catch (error: any) {
                              console.error('Error updating decisionMaker:', error);
                              alert(`Failed to update Decision Maker: ${error?.data?.message || error?.message || 'Unknown error'}`);
                            }
                          }}
                          style={{ fontSize: '0.75rem' }}
                        />
                      </div>
                    )}
                    {renderEditableContactRow('contactStage', 'Contact Stage', CheckSquare, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('email', 'No email', Envelope, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('phone', 'No phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('workPhone', 'Work phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('cellPhone', 'Cell phone', Phone, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('assistantName', "Assistant's Name", Person, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('assistantPhone', "Assistant's Phone", Phone, { showAll: showAllPersonFields })}
                    {renderEditableContactRow('assistantEmail', "Assistant's Email", Envelope, { showAll: showAllPersonFields })}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ORGANIZATION</h6>
                      <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.75rem' }} onClick={() => setShowAllOrganizationFields(!showAllOrganizationFields)}>
                        {showAllOrganizationFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>

                    {renderEditableContactRow('organization', 'No organization', BuildingFill, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('contractRenewalDate', '+ Add Contract Renewal', Calendar, { showAll: true })}
                    {renderEditableContactRow('accountStage', 'Account Stage', Briefcase, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('address', 'Address', GeoAltFill, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('linkedInProfile', 'LinkedIn profile', Linkedin, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('numberOfEmployees', 'Number of employees', PeopleFill, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('annualRevenue', 'Annual revenue', CurrencyDollar, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('city', 'City', GeoAltFill, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('state', 'State', GeoAltFill, { showAll: showAllOrganizationFields })}
                    {renderEditableContactRow('zipCode', 'ZIP code', GeoAltFill, { showAll: showAllOrganizationFields })}
                  </div>
                </div>
                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        as={IconOnlyDropdownToggle}
                        id="contact-sidebar-actions"
                      >
                        <ThreeDotsVertical size={14} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={handleDeleteSelectedContact}
                          style={{ color: '#dc3545' }}
                          disabled={isDeletingContact}
                        >
                          Delete contact
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="archive-contact-tooltip">
                          {selectedContact && isContactArchived(selectedContact) ? 'Move to inbox' : 'Archive'}
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleArchiveSelectedContact}
                        disabled={isUpdatingContact}
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderColor: '#ced4da'
                        }}
                      >
                        <Archive size={14} />
                      </Button>
                    </OverlayTrigger>
                    <Dropdown align="end" drop="up">
                      <Dropdown.Toggle
                        variant="primary"
                        size="sm"
                        id="contact-convert-actions"
                        disabled={!selectedContact || isConvertingContact}
                        style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', width: 'auto', borderRadius: '10px' }}
                      >
                        <Folder className="me-1" size={12} />
                        {isConvertingContact ? 'Converting...' : 'Convert'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          minWidth: '240px',
                          padding: '0.35rem',
                          borderRadius: '12px',
                          border: '1px solid #e9ecef',
                          boxShadow: '0 14px 32px rgba(15, 23, 42, 0.14)'
                        }}
                      >
                        {([
                          { target: 'Lead' as const, label: 'Convert to Lead', color: '#6c757d' },
                          { target: 'Client' as const, label: 'Convert to Client', color: '#198754' },
                          { target: 'Deal' as const, label: 'Convert to Deal', color: '#0d6efd' },
                        ]).map((option, index) => (
                          <React.Fragment key={option.target}>
                            <Dropdown.Item
                              onClick={() => handleConvertSelectedContact(option.target)}
                              disabled={isConvertingContact}
                              className="d-flex align-items-center"
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                padding: '0.5rem 0.8rem',
                                borderRadius: '8px',
                                lineHeight: 1.2
                              }}
                            >
                              <Lightning size={14} className="me-2" style={{ color: option.color }} />
                              {option.label}
                            </Dropdown.Item>
                            {index < 2 && <Dropdown.Divider style={{ margin: '0.15rem 0' }} />}
                          </React.Fragment>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', overflowX: 'hidden' }}>
                <Tabs activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)} className="mb-3" style={{ fontSize: '0.75rem' }}>
                  <Tab eventKey="notes" title="Notes">
                    <div className="mb-3 note-editor-container" onClick={() => { if (noteEditorRef.current && !isNoteEditorFocused) noteEditorRef.current.focus(); }} style={{ backgroundColor: isNoteEditorFocused ? '#fff9e6' : '#ffffff', border: isNoteEditorFocused ? '2px solid #0d6efd' : '1px solid #dee2e6', borderRadius: '4px', transition: 'all 0.2s', cursor: 'text' }}>
                      <div ref={noteEditorRef} contentEditable onFocus={() => setIsNoteEditorFocused(true)} onBlur={() => setTimeout(() => { const active = document.activeElement; if (!active?.closest('.note-editor-container') && !currentNoteContent.trim()) setIsNoteEditorFocused(false); }, 200)} onInput={(e) => { const content = e.currentTarget.innerHTML; setCurrentNoteContent(content); if (content.trim() === '' || content === '<br>') e.currentTarget.setAttribute('data-placeholder', 'Take a note, @name...'); else e.currentTarget.removeAttribute('data-placeholder'); }} data-placeholder="Take a note, @name..." style={{ minHeight: '100px', padding: '0.75rem', fontSize: '0.75rem', outline: 'none', backgroundColor: 'transparent', position: 'relative' }} className="note-editor" />
                      {isNoteEditorFocused && (
                        <div className="d-flex align-items-center gap-1 p-2 border-top" style={{ backgroundColor: '#f8f9fa', fontSize: '0.75rem' }} onClick={(e) => e.preventDefault()}>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('bold'); }} style={{ color: '#6c757d' }}><TypeBold size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('italic'); }} style={{ color: '#6c757d' }}><TypeItalic size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('underline'); }} style={{ color: '#6c757d' }}><TypeUnderline size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); const url = prompt('Enter URL:'); if (url) executeCommand('createLink', url); }} style={{ color: '#6c757d' }}><Link45deg size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertText', '@'); }} style={{ color: '#6c757d' }}><At size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertUnorderedList'); }} style={{ color: '#6c757d' }}><ListUl size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('insertOrderedList'); }} style={{ color: '#6c757d' }}><ListOl size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('outdent'); }} style={{ color: '#6c757d' }} title="Decrease indent"><ArrowLeft size={14} /></Button>
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('indent'); }} style={{ color: '#6c757d' }} title="Increase indent"><ArrowRight size={14} /></Button>
                          <div className="vr" style={{ height: '20px', margin: '0 0.25rem' }} />
                          <Button variant="link" size="sm" className="p-1" onClick={(e) => { e.preventDefault(); executeCommand('strikeThrough'); }} style={{ color: '#6c757d' }}><TypeStrikethrough size={14} /></Button>
                          <div className="flex-grow-1" />
                          <Button variant="outline-secondary" size="sm" onClick={handleCancelNote} style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Cancel</Button>
                          <Button variant="primary" size="sm" onClick={handleSaveNote} disabled={!currentNoteContent.trim() && !noteEditorRef.current?.textContent?.trim()} style={{ fontSize: '0.7rem', padding: '0.25rem 0.5rem' }}>Save</Button>
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Focus</h6>
                      {(pinnedFocusNotes.length === 0 && upcomingFocusActivities.length === 0) ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No focus items yet.</p>
                      ) : (
                        <div className="mb-2">
                          {pinnedFocusNotes.map((note) => (
                            <div key={`focus-note-${note.id}`} className="d-flex align-items-start gap-2 mb-2">
                              <div
                                className="d-inline-flex align-items-center justify-content-center border"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '999px',
                                  backgroundColor: '#f8f9fa',
                                  color: '#0d6efd',
                                  marginTop: '2px'
                                }}
                              >
                                <Pin size={13} />
                              </div>
                              <div
                                className="flex-grow-1 border rounded"
                                style={{ backgroundColor: '#fff9e6', padding: '0.5rem 0.65rem' }}
                              >
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div
                                    className="fw-semibold"
                                    style={{ fontSize: '0.75rem' }}
                                    dangerouslySetInnerHTML={{ __html: note.content }}
                                  />
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0"
                                    style={{ color: '#0d6efd' }}
                                    onClick={() => handleTogglePinNote(note.id)}
                                    title="Unpin note"
                                  >
                                    <Pin size={12} />
                                  </Button>
                                </div>
                                <div className="text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(note.timestamp)} · {note.author}
                                </div>
                              </div>
                            </div>
                          ))}
                          {upcomingFocusActivities.slice(0, 3).map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            return (
                              <div key={meeting.id} className="d-flex align-items-start gap-2 mb-2">
                                <div
                                  className="d-inline-flex align-items-center justify-content-center border"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '999px',
                                    backgroundColor: '#f8f9fa',
                                    color: '#495057',
                                    marginTop: '2px'
                                  }}
                                >
                                  {renderScheduledActivityIcon(activityType)}
                                </div>
                                <div
                                  className="flex-grow-1 border rounded"
                                  style={{ backgroundColor: '#ffffff', padding: '0.5rem 0.65rem' }}
                                >
                                  <div className="d-flex align-items-start justify-content-between gap-2">
                                    <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                      {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                    </div>
                                    <Button variant="link" size="sm" className="p-0 text-muted">
                                      <ThreeDotsVertical size={12} />
                                    </Button>
                                  </div>
                                  <div className="d-flex flex-wrap align-items-center gap-2 text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                                    <span>{formatFocusDateLabel(meeting.startTime)} · PhiLL Kross</span>
                                    <span className="d-inline-flex align-items-center gap-1">
                                      <Person size={11} />
                                      {selectedContact ? `${selectedContact.firstName} ${selectedContact.lastName}` : ''}
                                    </span>
                                    {selectedContact?.organization && (
                                      <span className="d-inline-flex align-items-center gap-1">
                                        <BuildingFill size={11} />
                                        {selectedContact.organization}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <p className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>Scheduled activities, pinned notes, email drafts and scheduled emails will appear here.</p>
                      <div className="d-flex align-items-stretch mb-2">
                        <Dropdown
                          show={showScheduledActivityTypeDropdown}
                          onToggle={(isOpen) => setShowScheduledActivityTypeDropdown(isOpen)}
                        >
                          <Dropdown.Toggle
                            as={IconOnlyDropdownToggle}
                            id="scheduled-contact-activity-type-toggle"
                            style={{
                              width: '62px',
                              height: '31px',
                              borderTopRightRadius: 0,
                              borderBottomRightRadius: 0,
                              borderRight: '0',
                              gap: '0.25rem'
                            }}
                          >
                            {renderScheduledActivityIcon(scheduledActivityType)}
                            <ChevronDown
                              size={9}
                              style={{
                                transition: 'transform 0.2s',
                                transform: showScheduledActivityTypeDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
                              }}
                            />
                          </Dropdown.Toggle>
                          <Dropdown.Menu style={{ minWidth: '170px', padding: '0.25rem' }}>
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => {
                              const isSelected = option.type === scheduledActivityType;
                              return (
                                <Dropdown.Item
                                  key={option.type}
                                  onClick={() => handleSelectScheduledActivityType(option.type)}
                                  style={{
                                    fontSize: '0.75rem',
                                    borderRadius: '4px',
                                    padding: '0.4rem 0.55rem',
                                    backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                    color: isSelected ? '#fff' : '#212529'
                                  }}
                                >
                                  <div className="d-flex align-items-center justify-content-between gap-2">
                                    <span className="d-flex align-items-center gap-2">
                                      <span style={{ width: '14px', display: 'inline-flex', justifyContent: 'center' }}>
                                        {renderScheduledActivityIcon(option.type)}
                                      </span>
                                      <span>{option.label}</span>
                                    </span>
                                    {isSelected && <Check size={13} />}
                                  </div>
                                </Dropdown.Item>
                              );
                            })}
                          </Dropdown.Menu>
                        </Dropdown>
                        <Form.Control
                          size="sm"
                          value={scheduledActivityText}
                          onChange={(e) => setScheduledActivityText(e.target.value)}
                          style={{
                            fontSize: '0.7rem',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            marginLeft: '-1px'
                          }}
                        />
                      </div>
                      <div className="d-flex gap-1 mt-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in1h')}
                          disabled={isCreatingMeeting}
                        >
                          In 1h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('in3h')}
                          disabled={isCreatingMeeting}
                        >
                          In 3h
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('tomorrow')}
                          disabled={isCreatingMeeting}
                        >
                          Tomorrow
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('nextWeek')}
                          disabled={isCreatingMeeting}
                        >
                          Next week
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          style={{ fontSize: '0.65rem' }}
                          onClick={() => handleQuickScheduleActivity('other')}
                          disabled={isCreatingMeeting}
                        >
                          + Other
                        </Button>
                      </div>
                    </div>
                    <div>
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>History</h6>
                      <ListGroup variant="flush">
                        {notes.map((note) => (
                          <ListGroup.Item
                            key={note.id}
                            className="px-0 py-2"
                            style={{
                              fontSize: '0.75rem',
                              backgroundColor: '#fff9e6'
                            }}
                          >
                            <div className="d-flex align-items-start gap-2">
                              <FileText size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div dangerouslySetInnerHTML={{ __html: note.content }} style={{ fontSize: '0.75rem', marginBottom: '0.25rem' }} />
                                <div className="text-muted small" style={{ fontSize: '0.7rem' }}>{formatDate(note.timestamp)} · {note.author}</div>
                              </div>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="p-0"
                                  style={{ color: note.pinned ? '#0d6efd' : '#6c757d' }}
                                  onClick={() => handleTogglePinNote(note.id)}
                                  title={note.pinned ? 'Unpin note' : 'Pin note to Focus'}
                                >
                                  <Pin size={12} />
                                </Button>
                                <Dropdown align="end">
                                  <Dropdown.Toggle
                                    as={IconOnlyDropdownToggle}
                                    id={`contact-note-actions-${note.id}`}
                                    style={{
                                      width: '20px',
                                      height: '20px',
                                      border: 'none',
                                      backgroundColor: 'transparent',
                                      color: '#6c757d'
                                    }}
                                  >
                                    <ThreeDotsVertical size={12} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item
                                      onClick={() => handleDeleteNote(note.id)}
                                      style={{ fontSize: '0.75rem', color: '#dc3545' }}
                                    >
                                      Delete note
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                        <ListGroup.Item className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                          <div className="text-muted small" style={{ fontSize: '0.7rem' }}>Contact created</div>
                          <div style={{ fontSize: '0.75rem' }}>{selectedContact.createdAt ? formatDate(selectedContact.createdAt) : 'Unknown'} · PhiLL Kross</div>
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  </Tab>
                  <Tab eventKey="activity" title="Activity">
                    <div style={{ position: 'relative', display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                      <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                        <div className="mb-3">
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.title}
                            onChange={(e) => handleActivitySchedulerFieldChange('title', e.target.value)}
                            placeholder="Activity title"
                            style={{ fontSize: '0.95rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem' }}
                          />
                          <div className="d-flex gap-1 flex-wrap">
                            {SCHEDULE_ACTIVITY_OPTIONS.map((option) => (
                              <button
                                key={option.type}
                                type="button"
                                onClick={() => handleSelectDetailedActivityType(option.type)}
                                style={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: 6,
                                  border: '1px solid #dee2e6',
                                  backgroundColor: scheduledActivityType === option.type ? '#0d6efd' : '#fff',
                                  color: scheduledActivityType === option.type ? '#fff' : '#6c757d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  cursor: 'pointer',
                                }}
                                title={option.label}
                              >
                                {renderScheduledActivityIcon(option.type)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                          <Clock size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            type="date"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(0, 10) : ''}
                            onChange={(e) => {
                              const dateValue = e.target.value;
                              const startTime = activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : '12:00';
                              const endTime = activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : '13:00';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                startTime: `${dateValue}T${startTime}`,
                                endTime: `${dateValue}T${endTime}`,
                              }));
                            }}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          />
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.startTime ? activitySchedulerForm.startTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.startTime
                                ? activitySchedulerForm.startTime.slice(0, 10)
                                : (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('startTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                          <span style={{ color: '#6c757d' }}>-</span>
                          <Form.Control
                            type="time"
                            size="sm"
                            value={activitySchedulerForm.endTime ? activitySchedulerForm.endTime.slice(11, 16) : ''}
                            onChange={(e) => {
                              const dateValue = activitySchedulerForm.endTime
                                ? activitySchedulerForm.endTime.slice(0, 10)
                                : activitySchedulerForm.startTime?.slice(0, 10)
                                  || (getDateKeyInTimezone(new Date(), tenantTimezone) || '');
                              handleActivitySchedulerFieldChange('endTime', `${dateValue}T${e.target.value}`);
                            }}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Calendar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.priority}
                            onChange={(e) => handleActivitySchedulerFieldChange('priority', e.target.value)}
                            style={{ width: 140, fontSize: '0.75rem' }}
                          >
                            <option value="low">Low</option>
                            <option value="normal">Normal</option>
                            <option value="high">High</option>
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Briefcase size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.purposeTypeId}
                            onChange={(e) => handleActivitySchedulerFieldChange('purposeTypeId', e.target.value)}
                            style={{ flex: 1, maxWidth: 280, fontSize: '0.75rem' }}
                          >
                            {meetingPurposeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Form.Select>
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Link45deg size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Control
                            size="sm"
                            value={activitySchedulerForm.location}
                            onChange={(e) => handleActivitySchedulerFieldChange('location', e.target.value)}
                            placeholder="Add location"
                            style={{ flex: 1, fontSize: '0.75rem' }}
                          />
                        </div>

                        <div className="d-flex align-items-center gap-2 mb-2">
                          <Paperclip size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.videoCallProvider}
                            onChange={(e) => {
                              const provider = e.target.value as '' | 'zoom' | 'teams' | 'google-meet';
                              setActivitySchedulerForm((prev) => ({
                                ...prev,
                                videoCallProvider: provider,
                                meetingType: provider ? 'video' : ((scheduledActivityType === 'call' || scheduledActivityType === 'text') ? 'phone' : 'in-person'),
                                meetingUrl: provider ? prev.meetingUrl : '',
                              }));
                            }}
                            style={{ flex: 1, maxWidth: 260, fontSize: '0.75rem' }}
                          >
                            <option value="">Install video call integration</option>
                            <option value="zoom">Zoom meeting</option>
                            <option value="teams">Microsoft Teams meeting</option>
                            <option value="google-meet">Google Meet meeting</option>
                          </Form.Select>
                        </div>

                        {activitySchedulerForm.videoCallProvider && (
                          <div className="d-flex align-items-center gap-2 mb-2" style={{ marginLeft: 24 }}>
                            <Form.Control
                              size="sm"
                              value={activitySchedulerForm.meetingUrl}
                              onChange={(e) => handleActivitySchedulerFieldChange('meetingUrl', e.target.value)}
                              placeholder={
                                activitySchedulerForm.videoCallProvider === 'google-meet'
                                  ? 'Paste Google Meet link here'
                                  : activitySchedulerForm.videoCallProvider === 'zoom'
                                    ? 'Paste Zoom link here'
                                    : 'Paste Teams link here'
                              }
                              style={{ flex: 1, fontSize: '0.75rem' }}
                            />
                          </div>
                        )}

                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Tag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                          <Form.Select
                            size="sm"
                            value={activitySchedulerForm.freeBusy}
                            onChange={(e) => handleActivitySchedulerFieldChange('freeBusy', e.target.value)}
                            style={{ width: 100, fontSize: '0.75rem' }}
                          >
                            <option value="free">Free</option>
                            <option value="busy">Busy</option>
                          </Form.Select>
                          <OverlayTrigger placement="top" overlay={<Tooltip>Controls whether this activity blocks your schedule.</Tooltip>}>
                            <span style={{ color: '#6c757d', cursor: 'pointer', display: 'inline-flex' }}>
                              <InfoCircle size={16} />
                            </span>
                          </OverlayTrigger>
                        </div>

                        <div className="mb-3">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <FileText size={16} style={{ color: '#6c757d' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Notes</span>
                          </div>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            size="sm"
                            value={activitySchedulerForm.notes}
                            onChange={(e) => handleActivitySchedulerFieldChange('notes', e.target.value)}
                            placeholder="Notes are visible within the app, but not to event guests."
                            style={{ fontSize: '0.75rem', padding: '0.4rem', backgroundColor: '#fffef0', border: '1px solid #dee2e6' }}
                          />
                          <div style={{ fontSize: '0.65rem', color: '#6c757d', marginTop: '0.25rem' }}>
                            Notes are visible within the app, but not to event guests.
                          </div>
                        </div>

                        <div className="mb-2">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Person size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <Form.Control size="sm" value="You" readOnly style={{ flex: 1, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }} />
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <CurrencyDollar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityDealLeadDisplayLabel || activityDealLeadSearch}
                                onChange={(e) => {
                                  setActivityDealLeadSearch(e.target.value);
                                  setShowActivityDealLeadDropdown(true);
                                  if (activitySchedulerForm.dealId || activitySchedulerForm.leadId) {
                                    setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }));
                                  }
                                }}
                                onFocus={() => setShowActivityDealLeadDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityDealLeadDropdown(false), 200)}
                                placeholder="Deal or Lead"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {(activitySchedulerForm.dealId || activitySchedulerForm.leadId) && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, dealId: '', leadId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityDealLeadDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityDealsAndLeads.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No deals or leads found</div>
                                  ) : (
                                    filteredActivityDealsAndLeads.map((item) => (
                                      <div
                                        key={`${item.type}-${item.id}`}
                                        onClick={() => {
                                          if (item.type === 'deal') {
                                            setActivitySchedulerForm((prev) => ({ ...prev, dealId: item.id, leadId: '' }));
                                          } else {
                                            setActivitySchedulerForm((prev) => ({ ...prev, leadId: item.id, dealId: '' }));
                                          }
                                          setActivityDealLeadSearch('');
                                          setShowActivityDealLeadDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
                            <PeopleFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityContactDisplayLabel || activityPeopleSearch}
                                onChange={(e) => {
                                  setActivityPeopleSearch(e.target.value);
                                  setShowActivityPeopleDropdown(true);
                                  if (activitySchedulerForm.contactId) setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }));
                                }}
                                onFocus={() => setShowActivityPeopleDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityPeopleDropdown(false), 200)}
                                placeholder="People"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.contactId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, contactId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityPeopleDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityContacts.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No contacts found</div>
                                  ) : (
                                    filteredActivityContacts.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, contactId: item.id }));
                                          setActivityPeopleSearch('');
                                          setShowActivityPeopleDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                            <BuildingFill size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
                            <div style={{ flex: 1, position: 'relative' }}>
                              <Form.Control
                                size="sm"
                                value={activityOrganizationDisplayLabel || activityOrganizationSearch}
                                onChange={(e) => {
                                  setActivityOrganizationSearch(e.target.value);
                                  setShowActivityOrganizationDropdown(true);
                                  if (activitySchedulerForm.clientId) setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }));
                                }}
                                onFocus={() => setShowActivityOrganizationDropdown(true)}
                                onBlur={() => setTimeout(() => setShowActivityOrganizationDropdown(false), 200)}
                                placeholder="Organization"
                                style={{ width: '100%', fontSize: '0.75rem' }}
                              />
                              {activitySchedulerForm.clientId && (
                                <button
                                  type="button"
                                  onClick={() => setActivitySchedulerForm((prev) => ({ ...prev, clientId: '' }))}
                                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#6c757d' }}
                                >
                                  <X size={12} />
                                </button>
                              )}
                              {showActivityOrganizationDropdown && (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10, backgroundColor: '#fff', border: '1px solid #dee2e6', borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                                  {filteredActivityOrganizations.length === 0 ? (
                                    <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                                  ) : (
                                    filteredActivityOrganizations.map((item) => (
                                      <div
                                        key={item.id}
                                        onClick={() => {
                                          setActivitySchedulerForm((prev) => ({ ...prev, clientId: item.id }));
                                          setActivityOrganizationSearch('');
                                          setShowActivityOrganizationDropdown(false);
                                        }}
                                        style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                                        onMouseDown={(e) => e.preventDefault()}
                                      >
                                        {item.label}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      <OverlayTrigger
                        placement="top"
                        trigger={[]}
                        show={showCalendarToggleTooltip}
                        overlay={
                          <Tooltip id="contact-activity-calendar-toggle-tooltip" style={{ pointerEvents: 'none' }}>
                            {showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          </Tooltip>
                        }
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.currentTarget.blur();
                            setShowCalendarToggleTooltip(false);
                            setShowActivityCalendar((prev) => !prev);
                          }}
                          onMouseEnter={() => setShowCalendarToggleTooltip(true)}
                          onMouseLeave={() => setShowCalendarToggleTooltip(false)}
                          onFocus={() => setShowCalendarToggleTooltip(true)}
                          onBlur={() => setShowCalendarToggleTooltip(false)}
                          aria-label={showActivityCalendar ? 'Hide calendar' : 'Show calendar'}
                          style={{
                            position: 'absolute',
                            right: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                            top: 120,
                            transform: 'translate(50%, -50%)',
                            width: 42,
                            height: 42,
                            borderRadius: '999px',
                            border: '1px solid #ced4da',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 3,
                            transition: 'right 0.25s ease',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                          }}
                        >
                          {showActivityCalendar ? <ArrowRight size={16} /> : <ArrowLeft size={16} />}
                        </button>
                      </OverlayTrigger>

                      <div
                        style={{
                          width: showActivityCalendar ? ACTIVITY_CALENDAR_PANEL_WIDTH : 0,
                          maxWidth: '100%',
                          flex: '0 0 auto',
                          overflow: 'hidden',
                          opacity: showActivityCalendar ? 1 : 0,
                          transition: 'width 0.25s ease, opacity 0.2s ease',
                          borderLeft: showActivityCalendar ? '1px solid #dee2e6' : 'none',
                          paddingLeft: 0,
                        }}
                      >
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            paddingLeft: '0.5rem',
                            paddingRight: '0.35rem',
                            pointerEvents: showActivityCalendar ? 'auto' : 'none',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                              {toLocaleDateStringInTimezone(
                                getActivitySchedulerDisplayDate(),
                                'en-US',
                                { weekday: 'long', month: 'long', day: 'numeric' },
                                tenantTimezone
                              )}
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <button
                                type="button"
                                onClick={handleActivitySchedulerPrevDay}
                                style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                                title="Previous day"
                              >
                                <ArrowLeft size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={handleActivitySchedulerNextDay}
                                style={{ border: 'none', background: 'none', padding: '0.15rem', cursor: 'pointer', color: '#6c757d' }}
                                title="Next day"
                              >
                                <ArrowRight size={14} />
                              </button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={handleActivitySchedulerToday}
                                style={{ fontSize: '0.65rem', padding: '0.12rem 0.35rem', whiteSpace: 'nowrap' }}
                              >
                                Today
                              </Button>
                            </div>
                          </div>

                          <div style={{ display: 'flex', overflow: 'hidden', borderRadius: 4, border: '1px solid #dee2e6' }}>
                            <div style={{ width: 44, flexShrink: 0, backgroundColor: '#f8f9fa', padding: '0.25rem 0' }}>
                              {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                                <div
                                  key={`activity-hour-label-${hour}`}
                                  style={{
                                    height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                    fontSize: '0.65rem',
                                    color: '#6c757d',
                                    paddingLeft: '0.35rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {formatActivityCalendarHourLabel(hour)}
                                </div>
                              ))}
                            </div>
                            <div
                              onClick={handleActivityCalendarGridClick}
                              style={{ flex: 1, position: 'relative', minHeight: ACTIVITY_CALENDAR_VISIBLE_HOURS * ACTIVITY_CALENDAR_SLOT_HEIGHT, cursor: 'pointer' }}
                            >
                              {Array.from({ length: ACTIVITY_CALENDAR_VISIBLE_HOURS }, (_, i) => activityCalendarStartHour + i).map((hour) => (
                                <div
                                  key={`activity-hour-grid-${hour}`}
                                  style={{
                                    height: ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                    borderBottom: '1px solid #eee',
                                  }}
                                />
                              ))}
                              {(() => {
                                const displayDate = getActivitySchedulerDisplayDate();
                                const now = new Date(activityNowTick);
                                const isTodayLine =
                                  displayDate.getDate() === now.getDate()
                                  && displayDate.getMonth() === now.getMonth()
                                  && displayDate.getFullYear() === now.getFullYear();
                                const nowHour = now.getHours() + now.getMinutes() / 60;
                                const visibleStartHour = activityCalendarStartHour;
                                const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                                const topPx = isTodayLine && nowHour >= visibleStartHour && nowHour < visibleEndHour
                                  ? (nowHour - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT
                                  : -999;
                                return (
                                  <div
                                    style={{
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      top: topPx,
                                      height: 2,
                                      backgroundColor: '#dc3545',
                                      display: 'flex',
                                      alignItems: 'center',
                                      zIndex: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: '0.6rem',
                                        color: '#dc3545',
                                        backgroundColor: '#fff',
                                        padding: '0 0.2rem',
                                        marginLeft: 4,
                                      }}
                                    >
                                      {toLocaleTimeStringInTimezone(
                                        now,
                                        'en-US',
                                        { hour: 'numeric', minute: '2-digit' },
                                        tenantTimezone
                                      )}
                                    </span>
                                    <span
                                      style={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        backgroundColor: '#dc3545',
                                        marginLeft: 2,
                                      }}
                                    />
                                  </div>
                                );
                              })()}
                              {activitySchedulerForm.startTime && activitySchedulerForm.endTime && (() => {
                                const start = new Date(activitySchedulerForm.startTime);
                                const end = new Date(activitySchedulerForm.endTime);
                                const displayDate = getActivitySchedulerDisplayDate();
                                const sameDay =
                                  start.getDate() === displayDate.getDate()
                                  && start.getMonth() === displayDate.getMonth()
                                  && start.getFullYear() === displayDate.getFullYear();
                                if (!sameDay || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
                                let startHour = start.getHours() + start.getMinutes() / 60;
                                let endHour = end.getHours() + end.getMinutes() / 60;
                                if (endHour < startHour) endHour += 24;
                                const visibleStartHour = activityCalendarStartHour;
                                const visibleEndHour = activityCalendarStartHour + ACTIVITY_CALENDAR_VISIBLE_HOURS;
                                if (startHour >= visibleEndHour || endHour <= visibleStartHour) return null;
                                const top = (Math.max(startHour, visibleStartHour) - visibleStartHour) * ACTIVITY_CALENDAR_SLOT_HEIGHT;
                                const height = Math.max(
                                  (Math.min(endHour, visibleEndHour) - Math.max(startHour, visibleStartHour)) * ACTIVITY_CALENDAR_SLOT_HEIGHT,
                                  24
                                );
                                return (
                                  <div
                                    className="rounded"
                                    style={{
                                      position: 'absolute',
                                      left: 4,
                                      right: 4,
                                      top,
                                      height: Math.max(height, 24),
                                      backgroundColor: '#0d6efd',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      padding: '0 0.35rem',
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                      zIndex: 1,
                                    }}
                                  >
                                    {renderScheduledActivityIcon(scheduledActivityType)}
                                    {activitySchedulerForm.title || 'Activity'}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2" style={{ borderTop: '1px solid #dee2e6' }}>
                      <Form.Check
                        type="checkbox"
                        id="contact-activity-mark-done"
                        label="Mark as done"
                        checked={activitySchedulerForm.markAsDone}
                        onChange={(e) => handleActivitySchedulerFieldChange('markAsDone', e.target.checked)}
                        style={{ fontSize: '0.75rem', marginBottom: 0 }}
                      />
                      <div className="d-flex gap-2">
                        <Button variant="secondary" size="sm" onClick={handleCancelDetailedActivity}>
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveDetailedActivity}
                          disabled={isCreatingMeeting || !activitySchedulerForm.title || !activitySchedulerForm.startTime || !activitySchedulerForm.endTime}
                        >
                          {isCreatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Timeline</h6>
                      {contactActivityTimeline.length === 0 ? (
                        <p className="text-muted small" style={{ fontSize: '0.7rem' }}>No activities yet for this contact.</p>
                      ) : (
                        <ListGroup variant="flush">
                          {contactActivityTimeline.map((meeting) => {
                            const activityType = getScheduledActivityTypeFromMeeting(meeting);
                            const meetingStartMs = new Date(meeting.startTime).getTime();
                            const isUpcoming = !Number.isNaN(meetingStartMs) && meetingStartMs >= Date.now();
                            return (
                              <ListGroup.Item key={`activity-entry-${meeting.id}`} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                                <div className="d-flex align-items-start gap-2">
                                  <div
                                    className="d-inline-flex align-items-center justify-content-center border"
                                    style={{
                                      width: '30px',
                                      height: '30px',
                                      borderRadius: '999px',
                                      backgroundColor: '#f8f9fa',
                                      color: '#495057',
                                      marginTop: '2px'
                                    }}
                                  >
                                    {renderScheduledActivityIcon(activityType)}
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                      <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                        {meeting.title || SCHEDULE_ACTIVITY_LABELS[activityType]}
                                      </div>
                                      <Badge bg={isUpcoming ? 'primary' : 'secondary'} style={{ fontSize: '0.6rem' }}>
                                        {isUpcoming ? 'Upcoming' : 'Past'}
                                      </Badge>
                                    </div>
                                    <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                      {formatDate(meeting.startTime)}
                                    </div>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            );
                          })}
                        </ListGroup>
                      )}
                    </div>
                  </Tab>
                  <Tab eventKey="email" title="Email">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="small mb-0" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Linked threads
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => refetchContactEmailThreads()}
                        disabled={!contactEmailAddress || isRefreshingContactEmailThreads}
                        style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                      >
                        {isRefreshingContactEmailThreads ? <Spinner size="sm" animation="border" /> : 'Refresh'}
                      </Button>
                    </div>
                    {!contactEmailAddress ? (
                      <p className="text-muted small" style={{ fontSize: '0.7rem' }}>
                        Add an email address to this contact to link Unibox threads.
                      </p>
                    ) : isLoadingContactEmailThreads ? (
                      <div className="d-flex align-items-center gap-2 text-muted small" style={{ fontSize: '0.7rem' }}>
                        <Spinner size="sm" animation="border" />
                        Loading email threads...
                      </div>
                    ) : contactEmailThreadsError ? (
                      <Alert variant="danger" className="py-2 px-3 mb-0" style={{ fontSize: '0.7rem' }}>
                        Failed to load email threads.
                      </Alert>
                    ) : contactEmailThreads.length === 0 ? (
                      <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>
                        No email threads found for {contactEmailAddress}.
                      </p>
                    ) : (
                      <ListGroup variant="flush">
                        {contactEmailThreads.map((thread) => (
                          <ListGroup.Item key={thread.id} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                            <div className="d-flex align-items-start gap-2">
                              <Envelope size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                    {thread.subject}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {thread.unreadCount > 0 && (
                                      <Badge bg="primary" style={{ fontSize: '0.6rem' }}>
                                        {thread.unreadCount} unread
                                      </Badge>
                                    )}
                                    {thread.messageCount > 1 && (
                                      <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                                        {thread.messageCount} msgs
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(thread.latestDate)} · {thread.latestFrom || 'Unknown sender'}
                                </div>
                                <div className="mt-2 d-flex flex-column gap-2">
                                  {thread.messages.map((message) => (
                                    <div key={`${thread.id}-${message.id}`} className="border-start ps-2">
                                      <div className="text-muted small" style={{ fontSize: '0.67rem' }}>
                                        {formatDate(message.date)} · {message.from || 'Unknown sender'}
                                      </div>
                                      <div className="small mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                        {message.preview || 'No preview available.'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Tab>
                  <Tab eventKey="files" title="Files">
                    <p className="text-muted small" style={{ fontSize: '0.7rem' }}>Files will appear here.</p>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Customize Columns Modal */}
      <Modal show={showCustomizeColumnsModal} onHide={() => setShowCustomizeColumnsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Customize columns</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Column customization coming soon...</p>
        </Modal.Body>
      </Modal>
      </div>
    </Container>
  );
};

export default Contacts;
